import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal, flushSync } from 'react-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pageCache } from '../utils/pageCache';
import {
  Plus, Search, X, Save, Loader2,
  Package, Wrench, Tag,
  Check, Ban, SendHorizontal,
  ShoppingCart, Trash2, RefreshCw, CreditCard, Building2, GripVertical,
  AlignLeft, Layers,
  Mail, Printer, ChevronDown, RotateCcw, Clock, FileText, Send, GitBranch,
  PenLine, Link, Upload, CheckCircle2, Truck, ChevronRight, UserPlus, Copy,
  Tablet, Monitor, Pencil, FolderOpen, ArrowRight, UserCheck, User, Receipt, Download,
  AlertTriangle,
} from 'lucide-react';
import { ActivitesFichePanel, useHasActivite, ActivityPulse } from '../components/ActivitesFichePanel';import { ModalNouvelleDiscussion } from '../components/ModalNouvelleDiscussion';
import { ClientSearchField } from '../components/ClientSearchField';
import { checkSiren } from '../utils/checkSiren';
import { BlocageClientModal } from '../components/BlocageClientModal';
import EcheancesTab from '../components/EcheancesTab';

import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'sonner';
import * as api from '../api';
import { SmartButton } from '../components/SmartButton';
import { SidePanel } from '../components/SidePanel';
import DocumentsTab from '../components/DocumentsTab';
import { useSociete } from '../context/SocieteContext';
import { useAuth } from '../context/AuthContext';
import { NumericInput } from '../components/NumericInput';
import { AggregationCAModal } from '../components/AggregationCAModal';
import ReglementPanel from '../components/ReglementPanel';
import RichTextEditor from '../components/RichTextEditor';
import EmailComposeModal from '../components/EmailComposeModal';

// ─── Constantes ──────────────────────────────────────────────────────────────

const STATUTS = {
  devis:        { label: 'Devis',         bg: '#F1F5F9', color: '#475569', border: '#CBD5E1' },
  devis_envoye: { label: 'Devis envoyé',  bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  commande:     { label: 'Commande',      bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
  annule:       { label: 'Annulé',        bg: '#FEF2F2', color: '#DC2626', border: '#FECACA' },
};

const FACT_STATUTS_CONFIG = {
  non_facturee:        { label: 'Non facturée',  bg: '#F8FAFC', color: '#64748B', border: '#CBD5E1' },
  en_attente_paiement: { label: 'En attente',    bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA' },
  validee:             { label: 'Validée',       bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  payee:               { label: 'Payée',         bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
};

const LIVR_STATUTS_CONFIG = {
  non_livree: { label: 'Non livrée', bg: '#F8FAFC', color: '#64748B', border: '#CBD5E1' },
  en_cours:   { label: 'En cours',  bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA' },
  livre:      { label: 'Livrée',    bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
};

const TVA_RATES_DEFAULT = [
  { taux: 0, designation: '0%' },
  { taux: 5.5, designation: '5,5%' },
  { taux: 10, designation: '10%' },
  { taux: 20, designation: '20%' },
];

/**
 * Résout le taux TVA à appliquer pour un article.
 * Priorité : tva_type_id de la famille → correspondance dans tvaRates → premier taux disponible → 20 par défaut.
 */
function _resolveTauxTva(tvaTypeId, tvaRates) {
  if (tvaTypeId && tvaRates.length > 0) {
    const match = tvaRates.find(r => r.tva_type_id === tvaTypeId);
    if (match) return match.taux;
  }
  return tvaRates.length > 0 ? tvaRates[0].taux : 20;
}

const TYPE_ICONS = {
  materiel:      <Package size={13} />,
  piece_detachee:<Tag size={13} />,
  prestation:    <Wrench size={13} />,
  location:      <ShoppingCart size={13} />,
  main_oeuvre:   <Wrench size={13} />,
};

function fmt(n) {
  return (Number(n) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d + (d.length === 10 ? 'T00:00:00' : '')).toLocaleDateString('fr-FR');
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function validiteDefault() {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

// ─── Badge statut ─────────────────────────────────────────────────────────────

function StatutBadge({ statut }) {
  const s = STATUTS[statut] || STATUTS.devis;
  return (
    <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full"
      style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
      {s.label}
    </span>
  );
}

// ─── StockPopover — stock par dépôt + bouton transfert ───────────────────────
function StockPopover({ article, emplacements, onClose, onTransfert, rect }) {
  const ref = useRef(null);
  const pos = rect ? {
    bottom: window.innerHeight - rect.top + 8,
    left: Math.min(Math.max(rect.left - 24, 8), window.innerWidth - 520),
  } : null;

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);

  const depotMap = {};
  emplacements.forEach(e => {
    if (!depotMap[e.stock_id])
      depotMap[e.stock_id] = { stock_id: e.stock_id, nom: e.stock_nom, agence: e.agence_nom || '', qty: 0 };
    depotMap[e.stock_id].qty += e.quantite;
  });
  const depots = Object.values(depotMap);
  const grandTotal = depots.reduce((s, d) => s + d.qty, 0);

  if (!pos) return null;

  return createPortal(
    <div ref={ref} className="fixed z-[500] bg-white rounded-xl shadow-2xl border overflow-hidden"
      style={{ bottom: pos.bottom, left: pos.left, borderColor: 'var(--border)', minWidth: 320, maxWidth: 560 }}>
      <div className="flex items-center justify-between px-3 py-2.5 border-b"
        style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
          Stock disponible par dépôt
        </p>
        <button onClick={onClose} className="p-1 rounded hover:bg-gray-200 ml-4"><X size={13} /></button>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs border-collapse">
          <thead>
            <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border)' }}>
              <th className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--text-muted)', borderRight: '1px solid var(--border)' }}>Article</th>
              {depots.map(d => (
                <th key={d.stock_id} className="px-4 py-2 text-center font-semibold whitespace-nowrap"
                  style={{ color: 'var(--text-muted)', borderRight: depots.length > 1 ? '1px solid var(--border)' : 'none' }}>
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="flex items-center gap-1"><Building2 size={10} />{d.nom}</span>
                    {d.agence && <span className="font-normal" style={{ color: 'var(--text-placeholder)' }}>{d.agence}</span>}
                  </div>
                </th>
              ))}
              {depots.length > 1 && <th className="px-4 py-2 text-center font-semibold" style={{ color: 'var(--text-muted)' }}>Total</th>}
            </tr>
          </thead>
          <tbody>
            <tr style={{ background: 'white' }}>
              <td className="px-3 py-3 font-medium" style={{ borderRight: '1px solid var(--border)', color: 'var(--text-secondary)', maxWidth: 200 }}>
                <p className="font-semibold truncate" style={{ color: 'var(--text)' }}>{article.designation}</p>
                {article.reference && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{article.reference}</p>}
              </td>
              {depots.map(d => (
                <td key={d.stock_id} className="px-4 py-3 text-center"
                  style={{ borderRight: depots.length > 1 ? '1px solid var(--border)' : 'none' }}>
                  <button
                    onClick={() => onTransfert({ article, stock: d })}
                    title="Demander un transfert depuis ce dépôt"
                    className="font-bold tabular-nums text-base rounded-lg px-3 py-1.5 transition-all hover:scale-105 active:scale-95"
                    style={{ color: 'var(--primary-solid)', background: 'var(--primary-50)', border: 'none', cursor: 'pointer' }}>
                    {d.qty.toLocaleString('fr-FR')}
                  </button>
                </td>
              ))}
              {depots.length > 1 && (
                <td className="px-4 py-3 text-center font-bold text-base tabular-nums" style={{ color: 'var(--text)' }}>
                  {grandTotal.toLocaleString('fr-FR')}
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>
      <div className="px-3 py-2 border-t text-xs" style={{ borderColor: 'var(--border)', color: 'var(--text-placeholder)' }}>
        Cliquez sur une quantité pour demander un transfert depuis ce dépôt
      </div>
    </div>,
    document.body
  );
}

// ─── TransfertModal — demande de transfert de stock ──────────────────────────
function TransfertModal({ article, stockSource, stocks, onClose, onSubmit }) {
  const [form, setForm] = useState({ stock_dest_id: '', quantite: '', commentaire: '' });
  const [saving, setSaving] = useState(false);
  const availableQty = stockSource?.qty || 0;
  const destStocks = stocks.filter(s => s.stock_id !== stockSource?.stock_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.stock_dest_id) return toast.error('Sélectionnez un dépôt destination');
    if (!form.quantite || Number(form.quantite) <= 0) return toast.error('Quantité invalide');
    if (Number(form.quantite) > availableQty) return toast.error(`Quantité max disponible : ${availableQty}`);
    setSaving(true);
    try { await onSubmit({ ...form, quantite: Number(form.quantite) }); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[600] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div>
            <p className="font-semibold" style={{ color: 'var(--text)' }}>Demande de transfert</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{article?.designation}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="rounded-xl p-3 border" style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)' }}>
            <p className="text-xs font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Source</p>
            <div className="flex items-center gap-2">
              <Building2 size={14} style={{ color: 'var(--primary-solid)' }} />
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{stockSource?.nom}</p>
                {stockSource?.agence && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{stockSource.agence}</p>}
              </div>
              <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-lg" style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                {availableQty} disponible{availableQty > 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <div className="flex justify-center"><ArrowRight size={18} style={{ color: 'var(--text-disabled)' }} /></div>
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Dépôt destination</label>
            {destStocks.length === 0 ? (
              <p className="text-xs py-2" style={{ color: 'var(--text-muted)' }}>Aucun autre dépôt disponible dans votre organisation.</p>
            ) : (
              <select value={form.stock_dest_id} onChange={e => setForm(f => ({ ...f, stock_dest_id: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }}>
                <option value="">— Sélectionner un dépôt —</option>
                {destStocks.map(s => <option key={s.stock_id} value={s.stock_id}>{s.nom}{s.agence_nom ? ` (${s.agence_nom})` : ''}</option>)}
              </select>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
              Quantité demandée <span style={{ color: 'var(--text-muted)' }}>(max {availableQty})</span>
            </label>
            <input type="number" min="1" max={availableQty} step="1"
              value={form.quantite} onChange={e => setForm(f => ({ ...f, quantite: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border)' }} placeholder="Ex: 2" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>Commentaire (optionnel)</label>
            <textarea value={form.commentaire} onChange={e => setForm(f => ({ ...f, commentaire: e.target.value }))}
              rows={2} className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
              style={{ borderColor: 'var(--border)' }} placeholder="Motif du transfert…" />
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 rounded-xl border text-sm font-medium"
              style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>Annuler</button>
            <button type="submit" disabled={saving || destStocks.length === 0}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
              style={{ background: saving ? 'var(--text-disabled)' : 'var(--primary-solid)' }}>
              <Send size={14} />
              {saving ? 'Envoi…' : 'Envoyer la demande'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Sélecteur article ───────────────────────────────────────────────────────

// ─── Ghost row de recherche d'articles dans le tableau ───────────────────────

// ─── Couleurs par type d'article ──────────────────────────────────────────────
const TYPE_DOT_COLORS_V = {
  materiel:       '#4F46E5',
  piece_detachee: '#EA580C',
  prestation:     '#16A34A',
  main_oeuvre:    '#9333EA',
  texte:          '#D97706',
  section:        '#6366F1',
};
const TYPE_REF_COLORS_V = {
  materiel:       { bg: '#EEF2FF', color: '#4338CA' },
  piece_detachee: { bg: '#FFF7ED', color: '#C2410C' },
  prestation:     { bg: '#F0FDF4', color: '#166534' },
  main_oeuvre:    { bg: '#FDF4FF', color: '#7E22CE' },
};

// ─── TypeSelectDropdown (Ventes) ─────────────────────────────────────────────
function TypeSelectDropdownV({ value, onChange, opts }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef();
  const current = opts.find(o => o.value === value) || opts[0];
  const dotColor = TYPE_DOT_COLORS_V[value] || '#9CA3AF';

  const toggle = (e) => {
    e.stopPropagation();
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left });
    setOpen(o => !o);
  };

  return (
    <>
      <button ref={btnRef} type="button" onClick={toggle}
        className="flex items-center gap-1.5 text-xs border rounded-md px-2 flex-shrink-0 transition-colors"
        style={{ height: 28, borderColor: '#E6E6E1', color: '#6B7280', background: '#fff', whiteSpace: 'nowrap', cursor: 'pointer' }}>
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dotColor }} />
        <span>{current?.label || 'Tous types'}</span>
        <ChevronDown size={9} style={{ opacity: 0.6 }} />
      </button>
      {typeof document !== 'undefined' && createPortal(
        <>
          {open && (
            <div data-radix-portal="" style={{ position: 'fixed', inset: 0, zIndex: 10000, cursor: 'default', pointerEvents: 'auto' }}
              onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          )}
          {open && pos && (
            <div data-radix-portal="" style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 10001,
              background: '#fff', border: '1px solid #E6E6E1', borderRadius: 10,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)', overflow: 'hidden', minWidth: 160, pointerEvents: 'auto' }}>
              {opts.map(o => {
                const dc = TYPE_DOT_COLORS_V[o.value] || '#9CA3AF';
                return (
                  <button key={o.value} type="button"
                    onClick={(e) => { e.stopPropagation(); onChange(o.value); setOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors"
                    style={{ fontWeight: o.value === value ? 600 : 400,
                             background: o.value === value ? '#F8FAFC' : 'transparent',
                             cursor: 'pointer' }}>
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dc }} />
                    <span style={{ color: '#374151' }}>{o.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </>,
        document.body
      )}
    </>
  );
}

function GhostRow({ onSelect, colSpan, emplacements = {}, activeAgences = [] }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState(null);
  const [typeFilter, setTypeFilter] = useState('');
  const inputRef = useRef();
  const portalRef = useRef();

  const search = useCallback(async (q, type) => {
    setLoading(true);
    try {
      const params = { limit: 50, sort_by: 'reference', sort_dir: 1 };
      if (q && q.trim()) params.q = q.trim();
      if (type) params.type = type;
      else params.exclude_type = 'location';
      const r = await api.getArticles(params);
      setResults((r.data.items || []).filter(a => a.type !== 'location'));
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => search(query, typeFilter), 250);
    return () => clearTimeout(t);
  }, [query, typeFilter, search]);

  useEffect(() => {
    const h = (e) => {
      const inRow = inputRef.current?.closest('tr')?.contains(e.target);
      const inPortal = portalRef.current?.contains(e.target);
      if (!inRow && !inPortal) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const calcPos = () => {
    if (!inputRef.current) return;
    const r = inputRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 520) });
  };

  const select = (art) => {
    onSelect(art);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const TYPE_OPTS = [
    { value: '',              label: 'Tous types' },
    { value: 'materiel',      label: 'Matériel' },
    { value: 'piece_detachee',label: 'Pièce détachée' },
    { value: 'prestation',    label: 'Prestation' },
    { value: 'main_oeuvre',   label: 'Main d\'œuvre' },
    { value: 'texte',         label: 'Commentaire' },
    { value: 'section',       label: 'Section' },
  ];

  const isSpecial = typeFilter === 'texte' || typeFilter === 'section';

  const placeholder = typeFilter === 'section'
    ? 'Titre de la section…'
    : typeFilter === 'texte'
      ? 'Saisissez votre texte…'
      : 'Rechercher un article…';

  const handleKeyDown = (e) => {
    if (isSpecial && e.key === 'Enter') {
      e.preventDefault();
      select({ type_ligne: typeFilter, designation: query });
      setQuery('');
      setTypeFilter('');
      setOpen(false);
    }
    if (!isSpecial && e.key === 'Enter' && results.length > 0) {
      select(results[0]);
    }
  };

  const TYPE_COLOR = {
    materiel:      { bg: '#EEF2FF', color: '#4F46E5' },
    piece_detachee:{ bg: '#FFF7ED', color: '#C2410C' },
    prestation:    { bg: '#F0FDF4', color: '#16A34A' },
    location:      { bg: '#F0F9FF', color: '#0369A1' },
    main_oeuvre:   { bg: '#FDF4FF', color: '#9333EA' },
  };

  const showDropdown = !isSpecial && open && pos;

  return (
    <>
      <tr style={{ borderTop: '1px solid #E6E6E1' }}>
        <td colSpan={colSpan} className="px-3 py-2">
          <div className="flex items-center gap-2">
            <Plus size={13} style={{ color: '#9CA3AF', flexShrink: 0 }} />
            <TypeSelectDropdownV value={typeFilter} opts={TYPE_OPTS} onChange={v => {
              setTypeFilter(v);
              setResults([]);
              setQuery('');
              if (v === 'texte' || v === 'section') {
                setOpen(false);
                setTimeout(() => inputRef.current?.focus(), 50);
              }
            }} />
            {/* Input avec loupe */}
            <div className="flex-1 flex items-center gap-1.5 rounded-lg border px-2.5 py-1 focus-within:ring-1 focus-within:ring-blue-400"
              style={{ borderColor: isSpecial ? (typeFilter === 'section' ? '#818CF8' : '#D97706') : '#E6E6E1', background: '#fff' }}>
              {isSpecial
                ? (typeFilter === 'section'
                    ? <Layers size={12} style={{ color: '#6366F1', flexShrink: 0 }} />
                    : <AlignLeft size={12} style={{ color: '#D97706', flexShrink: 0 }} />)
                : <Search size={12} style={{ color: '#9CA3AF', flexShrink: 0 }} />
              }
              <input
                ref={inputRef}
                value={query}
                onChange={e => { setQuery(e.target.value); if (!isSpecial) { setOpen(true); calcPos(); } }}
                onFocus={() => { if (!isSpecial) { setOpen(true); calcPos(); if (results.length === 0) search(query, typeFilter); } }}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                data-testid="vente-article-ghost-search"
                className="flex-1 text-sm bg-transparent focus:outline-none"
                style={{ color: isSpecial ? (typeFilter === 'section' ? '#3730A3' : '#78350F') : '#374151',
                         fontStyle: typeFilter === 'texte' ? 'italic' : 'normal',
                         fontWeight: typeFilter === 'section' ? 600 : 400 }}
              />
              {isSpecial && query.trim() && (
                <button type="button" onMouseDown={e => { e.preventDefault();
                  select({ type_ligne: typeFilter, designation: query });
                  setQuery(''); setTypeFilter(''); setOpen(false); }}
                  className="text-xs px-2 py-0.5 rounded font-medium flex-shrink-0"
                  style={{ background: typeFilter === 'section' ? '#EEF2FF' : '#FEF3C7',
                           color: typeFilter === 'section' ? '#4F46E5' : '#92400E' }}>
                  Ajouter
                </button>
              )}
              {!isSpecial && loading && <Loader2 size={12} className="animate-spin flex-shrink-0" style={{ color: '#9CA3AF' }} />}
            </div>
          </div>
        </td>
      </tr>

      {showDropdown && createPortal(
        <div
          ref={portalRef}
          style={{
            position: 'fixed', top: pos.top, left: pos.left, width: pos.width,
            zIndex: 9999, background: '#fff', border: '1px solid #E6E6E1',
            borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
            maxHeight: 380, overflowY: 'auto',
          }}
        >
          {/* Résultats articles */}
          {loading && (
            <div className="px-4 py-3 flex items-center gap-2 text-sm" style={{ color: '#9CA3AF' }}>
              <Loader2 size={13} className="animate-spin" />Recherche…
            </div>
          )}
          {!loading && results.length === 0 && (
            <div className="px-4 py-3 text-sm" style={{ color: '#9CA3AF' }}>Aucun article trouvé</div>
          )}
          {results.map(art => {
            const tc = TYPE_COLOR[art.type] || { bg: '#F3F4F6', color: '#6B7280' };
            const artEmpl    = emplacements[art.article_id] || [];
            const activeIds  = activeAgences.map(ag => ag.agence_id);
            const stockAgence = artEmpl.length > 0
              ? artEmpl.filter(e => activeIds.includes(e.agence_id)).reduce((s, e) => s + (e.quantite || 0), 0)
              : null;
            const stockGlobal = artEmpl.length > 0
              ? artEmpl.reduce((s, e) => s + (e.quantite || 0), 0)
              : (art.quantite_stock ?? null);
            return (
              <button
                key={art.article_id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); select(art); }}
                className="w-full text-left px-4 py-2.5 hover:bg-blue-50 transition-colors flex items-start gap-3 border-b last:border-0"
                style={{ borderColor: '#F3F4F6' }}
              >
                <span className="mt-0.5 flex-shrink-0" style={{ color: '#9CA3AF' }}>
                  {TYPE_ICONS[art.type] || <Tag size={13} />}
                </span>
                <div className="min-w-0 flex-1 flex items-center gap-2">
                  <span className="text-sm font-medium truncate" style={{ color: '#111827' }}>
                    {art.reference && <span className="font-mono text-xs mr-1" style={{ color: '#6B7280' }}>{art.reference} ·</span>}
                    {art.designation || art.reference}
                  </span>
                  {art.prix_vente > 0 && <span className="text-xs whitespace-nowrap flex-shrink-0" style={{ color: '#6B7280' }}>{fmt(art.prix_vente)} €</span>}
                  {art.gestion_stock && (
                    <span className="text-xs whitespace-nowrap flex-shrink-0 flex items-center gap-1">
                      <span style={{ color: (stockAgence ?? 0) > 0 ? '#374151' : '#DC2626', fontWeight: 600 }}>
                        Agence : {stockAgence ?? '—'}
                      </span>
                      {stockGlobal !== null && (
                        <span style={{ color: '#16A34A' }}>· Total : {stockGlobal}</span>
                      )}
                    </span>
                  )}
                  <span className="text-xs px-1.5 rounded font-medium flex-shrink-0" style={{ background: tc.bg, color: tc.color }}>{art.type}</span>
                </div>
              </button>
            );
          })}
        </div>,
        document.querySelector('[role="dialog"]') || document.body
      )}
    </>
  );
}

// ─── Ligne du tableau (Drag & Drop) ──────────────────────────────────────────

// ─── Composant Signature Bon de Commande ─────────────────────────────────────
// ─── Couleurs et config par mode de signature ────────────────────────────────
const SIG_MODES = {
  tablette:  { label: 'Signature tablette',   sub: 'Signer au doigt sur tablette tactile',       icon: Tablet,  color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' },
  ordinateur:{ label: 'Signature ordinateur', sub: 'Signer à la souris sur cet écran',           icon: Monitor, color: '#059669', bg: '#ECFDF5', border: '#A7F3D0' },
  email:     { label: 'Envoyer par email',    sub: 'PDF à signer et retourner',                  icon: Mail,    color: '#DC2626', bg: '#FEF2F2', border: '#FECACA' },
  lien:      { label: 'Envoyer un lien',      sub: 'Signature en ligne depuis le navigateur',    icon: Link,    color: '#0284C7', bg: '#F0F9FF', border: '#BAE6FD' },
};

// ─── Modal de signature ───────────────────────────────────────────────────────
function SignatureModal({ mode, devis, interlocuteurs: interlocuteursProp, onClose, onSigned }) {
  const cfg = SIG_MODES[mode];
  const Icon = cfg.icon;
  const [nom, setNom]         = useState(devis?.signature?.signataire_nom || devis?.contact_nom || '');
  // Utilise les interlocuteurs passés depuis VenteSidePanel (avec emails résolus), sinon fallback sur devis
  const resolvedInterlocuteurs = interlocuteursProp || devis?.interlocuteurs || [];
  const [email, setEmail]     = useState(
    devis?.signature?.signataire_email ||
    resolvedInterlocuteurs.find(i => i.email)?.email ||
    devis?.contact_email ||
    ''
  );
  const [loading, setLoading] = useState(false);
  const canvasRef  = useRef(null);
  const drawingRef = useRef(false);
  const lastPosRef = useRef(null);

  useEffect(() => {
    if (mode !== 'ordinateur') return;
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = cfg.color; ctx.lineWidth = 2.5; ctx.lineCap = 'round';
    const pos = (e) => { const r = canvas.getBoundingClientRect(); const t = e.touches?.[0] || e; return { x: t.clientX - r.left, y: t.clientY - r.top }; };
    const down = (e) => { e.preventDefault(); drawingRef.current = true; lastPosRef.current = pos(e); };
    const move = (e) => { e.preventDefault(); if (!drawingRef.current) return; const p = pos(e); ctx.beginPath(); ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y); ctx.lineTo(p.x, p.y); ctx.stroke(); lastPosRef.current = p; };
    const up = () => { drawingRef.current = false; };
    canvas.addEventListener('mousedown', down); canvas.addEventListener('mousemove', move); canvas.addEventListener('mouseup', up);
    canvas.addEventListener('touchstart', down, { passive: false }); canvas.addEventListener('touchmove', move, { passive: false }); canvas.addEventListener('touchend', up);
    return () => { ['mousedown','mousemove','mouseup'].forEach((ev,i) => canvas.removeEventListener(ev,[down,move,up][i])); ['touchstart','touchmove','touchend'].forEach((ev,i) => canvas.removeEventListener(ev,[down,move,up][i])); };
  }, [mode, cfg.color]);

  const handleSubmit = async () => {
    if (!nom.trim()) { toast.error('Saisissez le nom du signataire'); return; }
    if (mode === 'tablette') {
      // Envoi sur la tablette terrain — pas de canvas
      setLoading(true);
      try {
        const r = await api.envoyerSignatureTerrainVente(devis.vente_id, { signataire_nom: nom });
        toast.success('Devis envoyé sur la tablette terrain');
        onSigned?.(r.data);
        onClose();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Erreur envoi terrain');
      } finally { setLoading(false); }
      return;
    }
    if (mode !== 'ordinateur' && !email.trim()) { toast.error('Email requis'); return; }
    if (mode === 'ordinateur') {
      const canvas = canvasRef.current;
      const blank = document.createElement('canvas'); blank.width = canvas.width; blank.height = canvas.height;
      if (canvas.toDataURL() === blank.toDataURL()) { toast.error('Veuillez signer dans le cadre'); return; }
    }
    setLoading(true);
    try {
      let r;
      if (mode === 'ordinateur') r = await api.signerTabletteVente(devis.vente_id, { signataire_nom: nom, signature_image_b64: canvasRef.current.toDataURL('image/png') });
      else if (mode === 'email')  r = await api.envoyerBCEmail(devis.vente_id, { signataire_nom: nom, signataire_email: email });
      else                        r = await api.envoyerLienSignature(devis.vente_id, { signataire_nom: nom, signataire_email: email });
      toast.success(mode === 'ordinateur' ? 'Commande confirmée' : mode === 'email' ? 'Bon de commande envoyé' : 'Lien de signature envoyé');
      const updated = mode === 'lien' ? r.data.devis : r.data;
      // Avertissement visible si la création PL a échoué côté serveur
      const plWarn = updated?.pl_warning || r.data?.pl_warning;
      if (plWarn) toast.warning(plWarn, { duration: 10000 });
      onSigned?.(updated);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Erreur inconnue';
      toast.error(`Erreur : ${detail}`);
    } finally { setLoading(false); }
  };

  const inp = 'w-full text-sm px-3 py-2 rounded-lg border focus:outline-none transition';

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        style={{ border: `1px solid ${cfg.border}` }}
        onMouseDown={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ background: cfg.bg, borderBottom: `1px solid ${cfg.border}` }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: cfg.color }}>
              <Icon size={16} color="#fff" />
            </div>
            <div>
              <div className="text-sm font-bold" style={{ color: '#0F172A' }}>{cfg.label}</div>
              <div className="text-xs" style={{ color: '#64748B' }}>{cfg.sub}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: '#374151' }}>Nom du signataire</label>
            <input value={nom} onChange={e => setNom(e.target.value)} placeholder="Prénom Nom"
              className={inp} style={{ borderColor: '#E2E8F0' }} data-testid="sig-modal-nom" />
          </div>
          {mode !== 'ordinateur' && mode !== 'tablette' && (
            <div>
              <label className="block text-xs font-semibold mb-1.5" style={{ color: '#374151' }}>Adresse email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="client@entreprise.fr"
                className={inp} style={{ borderColor: '#E2E8F0' }} data-testid="sig-modal-email" />
            </div>
          )}
          {mode === 'tablette' && (
            <div className="rounded-xl px-4 py-3 text-sm" style={{ background: '#F5F3FF', borderLeft: '3px solid #7C3AED', color: '#5B21B6' }}>
              Le devis sera mis en attente et apparaîtra sur la <strong>tablette terrain</strong> (/terrain/signatures).
            </div>
          )}
          {mode === 'ordinateur' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold" style={{ color: '#374151' }}>Zone de signature</label>
                <button onClick={() => canvasRef.current?.getContext('2d').clearRect(0, 0, 460, 150)}
                  className="text-xs px-2 py-1 rounded-lg" style={{ color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}` }}>
                  Effacer
                </button>
              </div>
              <canvas ref={canvasRef} width={460} height={150}
                className="w-full rounded-xl touch-none cursor-crosshair"
                style={{ border: `2px dashed ${cfg.border}`, background: '#FAFAFA' }} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold border"
            style={{ borderColor: '#E2E8F0', color: '#64748B', background: '#fff' }}>
            Annuler
          </button>
          <button onClick={handleSubmit} disabled={loading} data-testid="sig-btn-submit"
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold text-white disabled:opacity-50 transition-all hover:opacity-90"
            style={{ background: cfg.color }}>
            {loading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            {mode === 'tablette' ? 'Envoyer sur tablette' : mode === 'ordinateur' ? 'Confirmer la signature' : mode === 'email' ? 'Envoyer le bon de commande' : 'Envoyer le lien'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Bouton "Confirmer la commande" avec dropdown ────────────────────────────
function SignatureCommande({ devis, interlocuteurs: interlocuteursProp, onSigned, onSigCancelled, onDocAdded }) {
  const [menuOpen, setMenuOpen]   = useState(false);
  const [modalMode, setModalMode] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [dragging, setDragging]   = useState(false);
  const [loading, setLoading]     = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const menuRef = useRef(null);
  const sig = devis?.signature || {};
  const en_attente = sig.statut === 'en_attente';

  useEffect(() => {
    if (!menuOpen) return;
    const h = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  const submitUpload = async () => {
    if (!uploadFile) { toast.error('Sélectionnez le PDF signé'); return; }
    setLoading(true);
    try { const fd = new FormData(); fd.append('fichier', uploadFile); const r = await api.uploadBCSigne(devis.vente_id, fd); toast.success('Commande confirmée'); onDocAdded?.(); onSigned?.(r.data); }
    catch { toast.error('Erreur lors du dépôt'); } finally { setLoading(false); }
  };

  if (en_attente && sig.methode === 'lien') return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl flex-shrink-0"
      style={{ background: '#FFF7ED', border: '1px solid #FED7AA' }}>
      <Clock size={13} style={{ color: '#D97706' }} />
      <span className="text-xs font-semibold whitespace-nowrap" style={{ color: '#92400E' }}>
        Lien envoyé — en attente de signature
      </span>
      <div className="flex items-center gap-1.5 ml-2">
        <button data-testid="sig-copier-lien"
          onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/ventes/signer/${sig.web_token}`); toast.success('Lien copié'); }}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
          style={{ background: '#D97706', color: '#fff' }}>
          <Copy size={12} /> Copier
        </button>
        <button data-testid="sig-renvoyer-lien" onClick={() => setModalMode('lien')}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
          style={{ background: '#FDE68A', color: '#92400E', border: '1px solid #FCD34D' }}>
          <Send size={12} /> Renvoyer
        </button>
        <button data-testid="sig-annuler-lien" disabled={cancelling}
          onClick={async () => {
            if (!window.confirm('Annuler le lien de signature ? Le devis repassera en brouillon éditable.')) return;
            setCancelling(true);
            try { const r = await api.annulerLienSignature(devis.vente_id); toast.success('Lien annulé'); onSigCancelled?.(r.data); }
            catch { toast.error('Erreur lors de l\'annulation'); } finally { setCancelling(false); }
          }}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
          style={{ background: '#FEE2E2', color: '#DC2626', border: '1px solid #FECACA' }}>
          {cancelling ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />} Annuler le lien
        </button>
      </div>
    </div>
  );

  if (en_attente && sig.methode === 'email') return (
    <div className="rounded-xl border p-3 space-y-2" style={{ borderColor: '#FDE68A', background: '#FFFBEB' }}>
      <div className="flex items-center gap-2">
        <Clock size={13} style={{ color: '#D97706' }} />
        <span className="text-xs font-semibold" style={{ color: '#92400E' }}>
          BC envoyé à {sig.signataire_email} — en attente du PDF signé
        </span>
      </div>
      {/* email upload */}
      <div className="space-y-2 pt-1">
          <div className="flex items-center gap-2">
            <label
              className="flex-1 flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border-2 border-dashed text-xs transition-colors"
              style={{
                borderColor: dragging ? '#22C55E' : (uploadFile ? '#22C55E' : '#D97706'),
                color: dragging ? '#15803D' : (uploadFile ? '#15803D' : '#92400E'),
                background: dragging ? '#F0FDF4' : '#FFFEF0',
              }}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={e => {
                e.preventDefault();
                setDragging(false);
                const f = e.dataTransfer.files?.[0];
                if (f) setUploadFile(f);
              }}
            >
              <Upload size={13} />
              {uploadFile ? uploadFile.name : (dragging ? 'Relâcher pour déposer' : 'Déposer le PDF signé')}
              <input type="file" accept=".pdf" className="hidden" onChange={e => setUploadFile(e.target.files[0])} />
            </label>
            <button onClick={submitUpload} disabled={!uploadFile || loading}
              className="px-3 py-2 rounded-lg text-xs font-semibold text-white disabled:opacity-50"
              style={{ background: '#15803D' }}>
              {loading ? '…' : 'Confirmer'}
            </button>
          </div>
          <button
            data-testid="sig-annuler-email"
            disabled={cancelling}
            onClick={async () => {
              if (!window.confirm('Annuler la demande de signature email ? Le devis repassera en brouillon éditable.')) return;
              setCancelling(true);
              try {
                const r = await api.annulerLienSignature(devis.vente_id);
                toast.success('Demande annulée — le devis est à nouveau éditable');
                onSigCancelled?.(r.data);
              } catch { toast.error('Erreur lors de l\'annulation'); }
              finally { setCancelling(false); }
            }}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
            style={{ background: '#FEE2E2', color: '#DC2626', border: '1px solid #FECACA' }}>
            {cancelling ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
            Annuler la demande de signature
          </button>
        </div>
    </div>
  );

  return (
    <>
      {/* Modal */}
      {modalMode && (
        <SignatureModal
          mode={modalMode}
          devis={devis}
          interlocuteurs={interlocuteursProp}
          onClose={() => setModalMode(null)}
          onSigned={(updated) => { setModalMode(null); onSigned?.(updated); }}
        />
      )}

      {/* Bouton dropdown */}
      <div className="relative" ref={menuRef}>
        <button
          data-testid="sig-btn-confirmer"
          onClick={() => setMenuOpen(o => !o)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
          style={{ background: '#059669' }}>
          <CheckCircle2 size={13} />
          Confirmer la commande
          <ChevronDown size={13} style={{ marginLeft: 2 }} />
        </button>
        {menuOpen && (
          <div className="absolute left-0 top-full mt-1.5 rounded-xl border bg-white shadow-xl z-50 overflow-hidden"
            style={{ borderColor: '#E6E6E1', minWidth: 235 }}>
            {Object.entries(SIG_MODES).map(([key, cfg], idx, arr) => {
              const Icon = cfg.icon;
              return (
                <button
                  key={key}
                  data-testid={`sig-btn-${key}`}
                  onClick={() => { setModalMode(key); setMenuOpen(false); }}
                  className={`w-full text-left flex items-center gap-3 px-4 py-3 transition-colors hover:bg-slate-50 ${idx < arr.length - 1 ? 'border-b' : ''}`}
                  style={{ borderColor: '#F3F4F6' }}>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}>
                    <Icon size={14} style={{ color: cfg.color }} />
                  </div>
                  <div>
                    <div className="text-sm font-medium" style={{ color: '#111827' }}>{cfg.label}</div>
                    <div className="text-xs" style={{ color: '#6B7280' }}>{cfg.sub}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

// ─── Cellule "En commande" — popover CA dans les ventes ──────────────────────
function VenteEnCommandeCell({ data, open, onToggle }) {
  const btnRef   = useRef(null);
  const navigate = useNavigate();
  const [pos, setPos] = useState(null);

  const handleOpen = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.top - 8, left: r.left + r.width / 2 });
    }
    onToggle();
  };

  if (!data || data.qte === 0) return <span className="text-xs" style={{ color: '#D1D5DB' }}>—</span>;

  return (
    <>
      <button ref={btnRef} type="button" onClick={handleOpen}
        data-testid="en-commande-vente-btn"
        className="text-xs font-bold cursor-pointer hover:opacity-70 transition-opacity"
        style={{ color: '#C2410C', background: 'none', border: 'none', padding: 0 }}>
        {data.qte}
      </button>

      <Dialog.Root open={open} onOpenChange={v => { if (!v) onToggle(); }}>
        <Dialog.Portal>
          <Dialog.Content
            onInteractOutside={onToggle}
            onEscapeKeyDown={onToggle}
            style={{
              position: 'fixed',
              top: pos ? pos.top : 0,
              left: pos ? pos.left : 0,
              transform: 'translate(-50%, -100%)',
              zIndex: 10000,
              background: '#fff',
              border: '1px solid #E5E7EB',
              borderRadius: 12,
              boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
              padding: 12,
              minWidth: 180,
              outline: 'none',
            }}
          >
            <Dialog.Title style={{ display: 'none' }}>Commandes d'achat</Dialog.Title>
            <p className="text-xs font-semibold mb-2" style={{ color: '#6B7280' }}>Commandes d'achat</p>
            <div className="flex flex-col gap-1">
              {(data.achats || []).map(a => (
                <button key={a.achat_id} type="button"
                  onClick={() => { onToggle(); navigate(`/achats?open_achat=${a.achat_id}`); }}
                  className="text-xs text-left px-2 py-1 rounded-lg hover:bg-blue-50 font-mono font-semibold"
                  style={{ color: '#1D4ED8', cursor: 'pointer' }}>
                  {a.numero}
                </button>
              ))}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

function LigneRow({ id, ligne, idx, onChange, onDelete, readOnly, emplacements, activeAgences, tvaRates, checkCell, enCommandeCell, uniteDecimals = {}, onStockClick, agenceId }) {
  const navigate = useNavigate();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const typeLigne = ligne.type_ligne || 'article';
  const isMateriel = ligne.type_article === 'materiel';
  const remiseCoeff = 1 - (Number(ligne.remise) || 0) / 100;
  const prixNetDerived = (Number(ligne.prix_unitaire_ht) || 0) * remiseCoeff;

  // ── Alerte stock engagé (pièces détachées uniquement) ──────────────────────
  const [stockAlerte, setStockAlerte] = useState(null);
  const alerteKeyRef = useRef(null);
  useEffect(() => {
    const key = `${ligne.article_id}:${ligne.quantite}:${agenceId}`;
    if (!ligne.article_id || isMateriel || alerteKeyRef.current === key) return;
    alerteKeyRef.current = key;
    api.getStockAlerte(ligne.article_id, Number(ligne.quantite) || 1, agenceId)
      .then(r => setStockAlerte(r.data))
      .catch(() => {});
  }, [ligne.article_id, ligne.quantite, isMateriel, agenceId]);

  // ── Prix Net local state : l'utilisateur tape → remise recalculée, PU NET affiché tel quel ──
  const [prixNetLocal, setPrixNetLocal] = useState(null);
  const [descOpen, setDescOpen] = useState(false); // doit être avant les retours conditionnels
  const prevPuHT = useRef(ligne.prix_unitaire_ht);
  // Réinitialise PU NET uniquement si PU HT change depuis l'extérieur
  if (ligne.prix_unitaire_ht !== prevPuHT.current) {
    prevPuHT.current = ligne.prix_unitaire_ht;
    if (prixNetLocal !== null) setPrixNetLocal(null);
  }
  const prixNetValue = prixNetLocal !== null ? prixNetLocal : prixNetDerived.toFixed(2);

  // Si l'utilisateur a forcé un P.U. NET, c'est lui qui sert de base au Total HT
  const prixNetPourCalc = prixNetLocal !== null ? (parseFloat(prixNetLocal) || 0) : prixNetDerived;
  const inp = `text-xs border rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 w-full`;
  const inpSt = { borderColor: 'var(--border)', color: 'var(--text)', background: 'var(--surface)' };
  const ht = (Number(ligne.quantite) || 0) * prixNetPourCalc;

  const rowStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.45 : 1,
    position: isDragging ? 'relative' : undefined,
    zIndex: isDragging ? 10 : 'auto',
    borderColor: 'var(--border-light)',
    background: isDragging ? 'var(--surface)' : undefined,
  };

  const dragHandle = !readOnly && (
    <td className="px-1 py-2 text-center align-middle" style={{ width: 30 }}>
      <span {...attributes} {...listeners} data-testid={`ligne-drag-handle-${idx}`}
        style={{ color: '#CBD5E1', cursor: isDragging ? 'grabbing' : 'grab', display: 'inline-flex', alignItems: 'center', touchAction: 'none' }}>
        <GripVertical size={14} />
      </span>
    </td>
  );

  const deleteBtn = !readOnly && (
    <td className="px-2 py-2 text-center align-middle" style={{ width: 36 }}>
      <button type="button" onClick={() => onDelete(idx)}
        className="p-1 rounded hover:bg-red-50 transition-colors" style={{ color: '#DC2626' }}>
        <Trash2 size={13} />
      </button>
    </td>
  );

  // ── Ligne SECTION ──────────────────────────────────────────────────────────
  if (typeLigne === 'section') {
    return (
      <tr ref={setNodeRef} className="border-b" style={{ ...rowStyle, background: isDragging ? 'var(--surface)' : '#EEF2FF' }}>
        {checkCell || dragHandle}
        <td colSpan={9} className="px-3 py-2 align-middle">
          {readOnly ? (
            <span className="text-xs font-bold tracking-wide uppercase" style={{ color: '#3730A3' }}>
              {ligne.designation || <span style={{ color: '#A5B4FC' }}>Section sans titre</span>}
            </span>
          ) : (
            <div className="flex items-center gap-2">
              <Layers size={13} style={{ color: '#6366F1', flexShrink: 0 }} />
              <input
                value={ligne.designation || ''}
                onChange={e => onChange(idx, 'designation', e.target.value)}
                placeholder="Titre de la section…"
                data-testid={`ligne-section-input-${idx}`}
                className="flex-1 text-xs font-bold bg-transparent focus:outline-none tracking-wide"
                style={{ color: '#3730A3' }}
                autoFocus={ligne.designation === ''}
              />
            </div>
          )}
        </td>
        {deleteBtn}
      </tr>
    );
  }

  // ── Ligne TEXTE / COMMENTAIRE ──────────────────────────────────────────────
  if (typeLigne === 'texte') {
    return (
      <tr ref={setNodeRef} className="border-b" style={{ ...rowStyle, background: isDragging ? 'var(--surface)' : '#FFFBEB' }}>
        {dragHandle}
        <td colSpan={9} className="px-3 py-2 align-middle">
          {readOnly ? (
            <span className="text-xs italic" style={{ color: '#78350F', whiteSpace: 'pre-wrap' }}>
              {ligne.designation || <span style={{ color: '#D97706' }}>—</span>}
            </span>
          ) : (
            <div className="flex items-start gap-2">
              <AlignLeft size={13} style={{ color: '#D97706', flexShrink: 0, marginTop: 2 }} />
              <textarea
                value={ligne.designation || ''}
                onChange={e => onChange(idx, 'designation', e.target.value)}
                placeholder="Saisissez votre texte… (Entrée pour sauter une ligne)"
                data-testid={`ligne-texte-input-${idx}`}
                rows={Math.max(1, (ligne.designation || '').split('\n').length)}
                className="flex-1 text-xs italic bg-transparent focus:outline-none resize-none leading-relaxed"
                style={{ color: '#78350F', minHeight: '1.4rem' }}
                autoFocus={ligne.designation === ''}
              />
            </div>
          )}
        </td>
        {deleteBtn}
      </tr>
    );
  }

  // ── Ligne ARTICLE (défaut) ─────────────────────────────────────────────────
  const artEmpl = (emplacements || {})[ligne.article_id] || [];
  const activeIds = (activeAgences || []).map(ag => ag.agence_id);
  const isGestion = artEmpl.length > 0 || ligne.gestion_stock;
  const stockAgence = artEmpl.filter(e => activeIds.includes(e.agence_id)).reduce((s, e) => s + (e.quantite || 0), 0);
  const stockTotal  = artEmpl.reduce((s, e) => s + (e.quantite || 0), 0);
  const stockAgenceDisplay = artEmpl.length > 0 ? stockAgence : (ligne.quantite_stock ?? null);
  const stockTotalDisplay  = artEmpl.length > 0 ? stockTotal  : (ligne.quantite_stock ?? null);
  const hasDescription = !!(ligne.description_html && ligne.description_html.trim() && ligne.description_html !== '<p></p>');

  const openArticle = () => {
    if (ligne.article_id) navigate(`/articles?open=${ligne.article_id}`);
  };

  return (
    <>
    <tr ref={setNodeRef} className="border-b" style={rowStyle}>
      {checkCell || dragHandle}

      {/* Référence — colonne fixe, cliquable */}
      <td className="px-2 py-1.5 align-middle" style={{ width: 156, minWidth: 156, maxWidth: 156 }}>
        {ligne.reference ? (() => {
          const rc = TYPE_REF_COLORS_V[ligne.type_article] || { bg: 'var(--primary-100, #EEF2FF)', color: 'var(--primary-solid)' };
          return (
            <button type="button" onClick={openArticle} title="Ouvrir la fiche article"
              className="font-mono text-xs px-1.5 py-0.5 rounded w-full text-left hover:opacity-75 transition-opacity cursor-pointer whitespace-nowrap overflow-hidden"
              style={{ background: rc.bg, color: rc.color, maxWidth: '100%' }}>
              {ligne.reference}
            </button>
          );
        })() : (
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>—</span>
        )}
      </td>

      {/* Désignation — texte gauche, truncate */}
      <td className="px-2 py-2 align-middle">
        <div className="flex items-start gap-1">
          <div className="flex-1 min-w-0">
            <span className="text-xs" style={{ color: 'var(--text)' }} title={ligne.designation}>
              {ligne.designation}
            </span>
            {isMateriel && (ligne.marque_nom || ligne.modele_nom) && (
              <div className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>
                {[ligne.marque_nom, ligne.modele_nom].filter(Boolean).join(' · ')}
              </div>
            )}
            {/* Alerte stock engagé */}
            {stockAlerte?.alerte && (
              <div className="mt-1 flex items-center gap-1 text-xs px-1.5 py-0.5 rounded"
                style={{ background: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A' }}>
                <span>⚠</span>
                <span>
                  Stock engagé — {stockAlerte.engage} cmdé · {stockAlerte.disponible} dispo
                </span>
              </div>
            )}
          </div>
          {/* Bouton description — visible en édition (même sans description) et en lecture si description existe */}
          {(!readOnly || hasDescription) && (
            <button type="button" onClick={() => setDescOpen(o => !o)}
              title={descOpen ? 'Masquer la description' : (hasDescription ? 'Afficher / modifier la description' : 'Ajouter une description technique')}
              style={{ color: descOpen ? 'var(--primary-solid)' : (hasDescription ? 'var(--text-muted)' : 'var(--text-disabled)'), flexShrink: 0, marginTop: 1 }}>
              <ChevronRight size={12} style={{ transform: descOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
            </button>
          )}
        </div>
      </td>

      {/* Quantité — éditable si !readOnly */}
      <td className="px-2 py-2 text-center align-middle" style={{ width: 70 }}>
        {readOnly ? (
          <span className="text-xs" style={{ color: 'var(--text)' }}>
            {Number(ligne.quantite).toLocaleString('fr-FR', {
              minimumFractionDigits: uniteDecimals[ligne.unite_symbole] ?? 0,
              maximumFractionDigits: uniteDecimals[ligne.unite_symbole] ?? 0
            })}
          </span>
        ) : (
        <NumericInput min={0} decimals={uniteDecimals[ligne.unite_symbole] ?? 3}
            value={ligne.quantite}
            onChange={v => onChange(idx, 'quantite', v)}
            className={inp} style={{ ...inpSt, width: 60, textAlign: 'center' }} />
        )}
      </td>

      {/* Stock */}
      <td className="px-2 py-2 text-center align-middle" style={{ width: 110, minWidth: 110 }}>
        {isGestion ? (
          <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
            <span className="text-xs font-semibold"
              style={{ color: (stockAgenceDisplay ?? 0) > 0 ? 'var(--text)' : '#DC2626' }}>
              {stockAgenceDisplay ?? 0}
            </span>
            {artEmpl.length > 0 ? (
              <button
                type="button"
                title={`${stockTotalDisplay ?? 0} unités au total dans tous les dépôts — cliquer pour détailler`}
                onClick={e => { e.stopPropagation(); onStockClick?.({ ligne, emplacements: artEmpl.filter(em => em.quantite > 0), rect: e.currentTarget.getBoundingClientRect() }); }}
                className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-bold transition-all hover:opacity-80 cursor-pointer"
                style={{ background: '#DCFCE7', color: '#15803D', border: 'none' }}>
                <Building2 size={9} />
                {stockTotalDisplay ?? 0}
              </button>
            ) : (
              <span className="flex items-center gap-0.5 text-xs font-semibold" style={{ color: '#16A34A' }}>
                <Building2 size={9} />
                {stockTotalDisplay ?? 0}
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs" style={{ color: '#9CA3AF' }}>—</span>
        )}
      </td>

      {/* En commande (CA) — injecté depuis le parent */}
      {enCommandeCell !== undefined && enCommandeCell}
      <td className="px-2 py-2 text-right align-middle" style={{ width: 100 }}>
        {readOnly ? (
          <span className="text-xs" style={{ color: 'var(--text)' }}>{fmt(ligne.prix_unitaire_ht)}</span>
        ) : (
          <NumericInput min={0}
            value={ligne.prix_unitaire_ht}
            onChange={v => onChange(idx, 'prix_unitaire_ht', v)}
            className={inp} style={{ ...inpSt, width: 90, textAlign: 'right' }} />
        )}
      </td>
      {/* Remise — toujours visible, toujours éditable */}
      <td className="px-2 py-2 align-middle" style={{ width: 75 }}>
        <div className="flex items-center gap-0.5">
          <NumericInput min={0} max={100}
            value={parseFloat((Number(ligne.remise) || 0).toFixed(2))}
            onChange={v => { setPrixNetLocal(null); onChange(idx, 'remise', v); }}
            className="text-xs border rounded px-1 py-0.5 focus:outline-none focus:ring-1 text-right"
            style={{ ...inpSt, width: 50 }}
            data-testid={`ligne-remise-${idx}`}
          />
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>%</span>
        </div>
      </td>
      {/* Prix Net — saisie directe → calcule la remise automatiquement */}
      <td className="px-2 py-2 text-right align-middle" style={{ width: 90 }}>
        {readOnly ? (
          <span className="text-xs" style={{ color: 'var(--text)' }}>{fmt(prixNetDerived)}</span>
        ) : (
          <input
            type="text"
            inputMode="decimal"
            value={prixNetLocal !== null ? prixNetLocal.replace('.', ',') : prixNetDerived.toFixed(2).replace('.', ',')}
            onFocus={e => e.target.select()}
            onChange={e => setPrixNetLocal(e.target.value.replace(',', '.'))}
            onBlur={() => {
              const val = parseFloat(prixNetLocal ?? prixNetDerived) || 0;
              const puHT = Number(ligne.prix_unitaire_ht);
              if (puHT > 0) {
                // Pleine précision : pas d'arrondi → les totaux du bas utilisent ce chiffre exact
                const newRemise = Math.max(0, Math.min(100, (1 - val / puHT) * 100));
                onChange(idx, 'remise', newRemise);
              }
              // On NE remet PAS prixNetLocal à null → la valeur saisie reste affichée
            }}
            className={inp}
            style={{ ...inpSt, width: 80, textAlign: 'right' }}
            data-testid={`ligne-prix-net-${idx}`}
          />
        )}
      </td>
      <td className="px-2 py-2 align-middle" style={{ width: 80 }}>
        {readOnly ? (
          <span className="text-xs" style={{ color: 'var(--text)' }}>{ligne.taux_tva}%</span>
        ) : (
          <select value={ligne.taux_tva} onChange={e => onChange(idx, 'taux_tva', parseFloat(e.target.value))}
            className="text-xs border rounded px-1 py-0.5 focus:outline-none w-full"
            style={{ borderColor: 'var(--border)', color: 'var(--text)', background: 'var(--surface)' }}>
            {(tvaRates && tvaRates.length > 0 ? tvaRates : TVA_RATES_DEFAULT).map(r => (
              <option key={r.taux} value={r.taux}>{r.designation || `${r.taux}%`}</option>
            ))}
          </select>
        )}
      </td>
      <td className="px-2 py-2 text-right font-semibold text-xs align-middle" style={{ color: 'var(--text)', width: 100 }}>
        {fmt(ht)}
      </td>
      {!readOnly && (
        <td className="px-2 py-2 text-center align-middle" style={{ width: 36 }}>
          <button type="button" onClick={() => onDelete(idx)}
            className="p-1 rounded hover:bg-red-50 transition-colors" style={{ color: '#DC2626' }}>
            <Trash2 size={13} />
          </button>
        </td>
      )}
    </tr>

    {/* ── Ligne description technique ─────────────────────────────────── */}
    {descOpen && (
      <tr style={{ background: '#F8FAFC', borderBottom: '1px solid var(--border)' }}>
        <td colSpan={99} className="px-4 pb-3 pt-1">
          <div style={{ borderLeft: '2px solid var(--primary-solid)', paddingLeft: 12 }}>
            {readOnly ? (
              <div
                className="description-html text-xs pt-1"
                style={{ color: 'var(--text-secondary)' }}
                dangerouslySetInnerHTML={{ __html: ligne.description_html }}
              />
            ) : (
              <div className="pt-1">
                <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
                  Description technique
                </p>
                <RichTextEditor
                  value={ligne.description_html || ''}
                  onChange={html => onChange(idx, 'description_html', html)}
                  minHeight="80px"
                  placeholder="Description technique, notes d'installation, caractéristiques…"
                />
              </div>
            )}
          </div>
        </td>
      </tr>
    )}
    </>
  );
}

// ─── Side Panel ───────────────────────────────────────────────────────────────

function _fmtDatetime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function _evtConfig(type) {
  const cfgs = {
    envoi_email:          { label: 'Email envoyé',           color: '#1D4ED8', bg: '#EFF6FF', border: '#BFDBFE', Icon: Mail },
    impression:           { label: 'Imprimé / PDF',          color: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB', Icon: Printer },
    revision_creee:       { label: 'Révision créée',         color: '#D97706', bg: '#FEF3C7', border: '#FDE68A', Icon: RotateCcw },
    revision_restauree:   { label: 'Version rappelée',       color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE', Icon: RotateCcw },
    nouvelle_version:     { label: 'Nouvelle version créée', color: '#0369A1', bg: '#E0F2FE', border: '#BAE6FD', Icon: GitBranch },
    modification_post_envoi: { label: 'Modifié après envoi', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', Icon: RefreshCw },
  };
  return cfgs[type] || { label: type, color: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB', Icon: Clock };
}

function VenteSidePanel({ devis, onClose, onSaved, uniteDecimals = {} }) {
  const isNew = !devis;
  const { activeSociete, activeAgences, societes, isReadOnly: orgReadOnly } = useSociete();

  // Carte agence_id → provider_comptable (pour le routing Odoo/Pennylane)
  const agenceProviderMap = useMemo(() => {
    const map = {};
    for (const soc of (societes || [])) {
      for (const ag of (soc.agences || [])) {
        map[ag.agence_id] = soc.provider_comptable || 'odoo';
      }
    }
    return map;
  }, [societes]);
  const bcProvider = 'pennylane';
  const nomProvider = 'Pennylane';
  const isPL = bcProvider === 'pennylane'; // Pennylane est le seul provider (Odoo retiré)
  const { user: currentUser } = useAuth();
  const [readOnly, setReadOnly] = useState(
    isNew ? orgReadOnly : (devis?.statut === 'commande' || devis?.statut === 'annule')
  );
  const [saving, setSaving] = useState(false);
  const [changingStatut, setChangingStatut] = useState(false);
  const [emplacements, setEmplacements] = useState({});
  const [tvaRates, setTvaRates] = useState([]);
  const [stockPopover, setStockPopover] = useState(null); // { ligne, emplacements, rect }
  const [allStocks, setAllStocks] = useState([]);
  const [transfertInit, setTransfertInit] = useState(null); // { article, stock }
  const [showDupliqueModal, setShowDupliqueModal] = useState(false);
  const [dupliqueAgenceId, setDupliqueAgenceId] = useState(null); // null = agence source

  // ── Tabs Contenu / Révisions / Activité / Documents ──
  const [activeTab, setActiveTab] = useState('contenu');
  const { hasActivity } = useHasActivite('vente', devis?.vente_id);
  const [docCount, setDocCount]   = useState(0);

  // ── Dropdown "Envoyer le devis" ──
  const [envoyerMenuOpen, setEnvoyerMenuOpen] = useState(false);
  const envoyerMenuRef = useRef();
  useEffect(() => {
    if (!envoyerMenuOpen) return;
    const h = (e) => { if (!envoyerMenuRef.current?.contains(e.target)) setEnvoyerMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [envoyerMenuOpen]);

  // ── Modal Email ──
  const [emailDevisOpen, setEmailDevisOpen] = useState(false);
  const [sendingMail, setSendingMail] = useState(false);

  // ── Révisions & Activité ──
  const [revisions, setRevisions] = useState([]);
  const [loadingTab, setLoadingTab] = useState(false);
  const [rappelConfirm, setRappelConfirm] = useState(null);

  // ── BL Ventes ──
  const [blCount, setBlCount]     = useState(null);
  const [creatingBL, setCreatingBL] = useState(false);
  const [blocageClient, setBlocageClient] = useState(null);
  const [showBlocageModal, setShowBlocageModal] = useState(false);
  // ── Facturation ──
  const [statutFacturation, setStatutFacturation] = useState(devis?.statut_facturation || 'non_facturee');
  const [facture_ext_status, setFactureExtStatus] = useState(devis?.facture_ext_status || null);
  const [facturant, setFacturant]         = useState(false);
  const [supprimantDraft, setSupprimantDraft]     = useState(false);
  // ── Pennylane draft PDF panel ──
  const [plDraftPanel, setPlDraftPanel]           = useState(null); // null | { loading, pdf_blob_url }

  const closePlDraftPanel = () => {
    if (plDraftPanel?.pdf_blob_url) URL.revokeObjectURL(plDraftPanel.pdf_blob_url);
    setPlDraftPanel(null);
  };

  const handleVoirBrouillonPennylane = async () => {
    if (!devis?.vente_id) return;
    setPlDraftPanel({ loading: true, pdf_blob_url: null });
    try {
      const r = await api.getPlDraftPdfBlob(devis.vente_id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      setPlDraftPanel({ loading: false, pdf_blob_url: url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de charger le brouillon Pennylane');
      setPlDraftPanel(null);
    }
  };

  const handleVoirFacturePennylane = async () => {
    if (!devis?.vente_id) return;
    setPlDraftPanel({ loading: true, pdf_blob_url: null });
    try {
      const [r, facsRes] = await Promise.all([
        api.getPlInvoicePdfBlob(devis.vente_id),
        factures.length === 0 ? api.listerFacturesBC(devis.vente_id).catch(() => null) : null,
      ]);
      if (facsRes?.data) setFactures(Array.isArray(facsRes.data) ? facsRes.data : facsRes.data?.items || []);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      setPlDraftPanel({ loading: false, pdf_blob_url: url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossible de charger la facture Pennylane');
      setPlDraftPanel(null);
    }
  };
  const [validantPL, setValidantPL]               = useState(false);
  const [supprimantDraftPL, setSupprimantDraftPL] = useState(false);

  const handleSupprimerBrouillonPennylane = async () => {
    if (!devis?.vente_id) return;
    if (!window.confirm('Supprimer ce brouillon Pennylane ?')) return;
    setSupprimantDraftPL(true);
    try {
      await api.supprimerPLDraft(devis.vente_id);
      toast.success('Brouillon supprimé');
      closePlDraftPanel();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression brouillon');
    } finally { setSupprimantDraftPL(false); }
  };

  const handleValiderPennylane = async () => {
    if (!devis?.vente_id) return;
    setValidantPL(true);
    try {
      const r = await api.validerPLDraft(devis.vente_id);
      toast.success(`Facture ${r.data?.ext_invoice_number || ''} validée dans Pennylane`);
      closePlDraftPanel();
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur validation Pennylane');
    } finally { setValidantPL(false); }
  };

  const handleAcquitteeVentes = async () => {
    const fac = factures?.[0];
    if (!fac?.facture_id) { toast.error('Aucune facture disponible'); return; }
    setDownloadingAcquittee(true);
    try {
      const r = await api.getFactureAcquitteeBlob(fac.facture_id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `facture-acquittee-${fac.ext_invoice_number || fac.facture_id}.pdf`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur téléchargement facture acquittée');
    } finally { setDownloadingAcquittee(false); }
  };
  const [showReglementPanel, setShowReglementPanel] = useState(false);
  const [reglementSaisi, setReglementSaisi]       = useState(null);
  const [envoyantFactureEmail, setEnvoyantFactureEmail] = useState(false);
  const [downloadingAcquittee, setDownloadingAcquittee] = useState(false);
  // ── Panel email facture ──
  const [factureEmailPanel, setFactureEmailPanel] = useState({ open: false, loading: false, destinataires: [] });

  const handleConfirmFactureEmail = async ({ emails, recipients, sujet, corps }) => {
    if (!devis) return;
    setEnvoyantFactureEmail(true);
    try {
      const r = await api.envoyerFactureEmailVente(devis.vente_id, { to_emails: emails, recipients, sujet, corps });
      toast.success(r.data?.message || `Email envoyé à ${emails.length} destinataire(s)`);
      setFactureEmailPanel({ open: false, loading: false, destinataires: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi de la facture");
    } finally {
      setEnvoyantFactureEmail(false);
    }
  };

  const handleEnvoyerFactureEmail = async () => {
    if (!devis?.vente_id) return;
    setFactureEmailPanel(p => ({ ...p, open: true, loading: true, destinataires: [] }));
    try {
      const r = await api.getDestinatairesFactureVente(devis.vente_id);
      setFactureEmailPanel(p => ({
        ...p,
        loading: false,
        destinataires: r.data?.destinataires || [],
        factureMeta:   r.data?.facture || null,
      }));
    } catch {
      setFactureEmailPanel(p => ({ ...p, loading: false, destinataires: [] }));
    }
  };
  const modeReglement = devis?.mode_reglement || 'en_compte';
  // ── Onglet Factures ──────────────────────────────────────────────────────
  const [factures, setFactures]               = useState([]);
  const [facturesLoading, setFacturesLoading] = useState(false);
  const [facturesCount, setFacturesCount]     = useState(0);
  const [creatingBLFac, setCreatingBLFac]     = useState(null); // facture_id en cours
  // ── Commandes d'achat depuis vente ──
  const [selectedCAIds, setSelectedCAIds]   = useState([]);
  const [enCommandeVente, setEnCommandeVente] = useState({});
  const [venteLinkedCAs, setVenteLinkedCAs] = useState([]);  // Smart buttons — toutes CAs liées
  const [sendingCA, setSendingCA]           = useState(false);
  const [aggregationModalVente, setAggregationModalVente] = useState(null); // { candidats, lignes, source_id, agence_id }
  const [caPopover, setCAPopover]           = useState(null); // article_id ouvert
  // ── Sélection matériel inline (lignes matériel sans materiel_id assigné) ──
  const [partialMaterielSelection, setPartialMaterielSelection] = useState({}); // { ligne_id: materiel_id }
  const [materielOptions, setMaterielOptions]                   = useState({}); // { article_id: [{materiel_id, numero_serie, annee}] }
  const [materielOptionsLoading, setMaterielOptionsLoading]     = useState({}); // { article_id: bool }

  // ── Utilisateurs de l'org (pour Suivi par) ────────────────────────────────
  const [orgUsers, setOrgUsers] = useState([]);
  useEffect(() => {
    api.getUsers().then(r => setOrgUsers((r.data || []).filter(u => u.actif))).catch(() => {});
  }, []);

  // ── Acheteur / Exploitant ──────────────────────────────────────────────────
  const [acheteurId,       setAcheteurId]       = useState(devis?.acheteur_id || '');
  const [acheteurNom,      setAcheteurNom]      = useState(devis?.acheteur_nom || '');
  const [exploitantBLId,   setExploitantBLId]   = useState(devis?.exploitant_bl_id || '');
  const [exploitantBLType, setExploitantBLType] = useState(devis?.exploitant_bl_type || 'entreprise');
  const [exploitantBLNom,  setExploitantBLNom]  = useState(devis?.exploitant_bl_nom || '');
  const [rechAcheteur,   setRechAcheteur]   = useState('');
  const [rechExploitant, setRechExploitant] = useState('');
  const [resAcheteur,    setResAcheteur]    = useState([]);
  const [resExploitant,  setResExploitant]  = useState([]);
  const [loadingAch,     setLoadingAch]     = useState(false);
  const [loadingExpl,    setLoadingExpl]    = useState(false);
  const [savingAE,       setSavingAE]       = useState(false);

  // Recherche acheteur
  useEffect(() => {
    if (!rechAcheteur || rechAcheteur.length < 2) { setResAcheteur([]); return; }
    const t = setTimeout(async () => {
      setLoadingAch(true);
      try {
        const r = await api.getEntreprises({ search: rechAcheteur, limit: 12 });
        const all = r.data.data || r.data || [];
        // Exclure les prospects du dropdown acheteur facturé
        const filtered = all.filter(e => {
          const types = e.types_entreprise || (e.type_entreprise ? [e.type_entreprise] : []);
          return !types.includes('prospect');
        });
        setResAcheteur(filtered.slice(0, 8));
      }
      catch { /* silent */ } finally { setLoadingAch(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [rechAcheteur]);

  // Recherche exploitant (entreprises + personnes)
  useEffect(() => {
    if (!rechExploitant || rechExploitant.length < 2) { setResExploitant([]); return; }
    const t = setTimeout(async () => {
      setLoadingExpl(true);
      try {
        const [re, rp] = await Promise.all([
          api.getEntreprises({ search: rechExploitant, limit: 5 }),
          api.getPersonnes({ search: rechExploitant, limit: 5 }),
        ]);
        const ents = (re.data.data || re.data || []).map(e => ({ ...e, _type: 'entreprise', _nom: e.nom }));
        const pers = (rp.data.data || rp.data || []).map(p => ({ ...p, _type: 'personne', _nom: `${p.prenom || ''} ${p.nom}`.trim() }));
        setResExploitant([...ents, ...pers]);
      } catch { /* silent */ } finally { setLoadingExpl(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [rechExploitant]);

  const saveAcheteurExploitant = async (patch) => {
    if (!devis?.vente_id) return;
    setSavingAE(true);
    try {
      await api.updateAcheteurExploitantBC(devis.vente_id, patch);
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    finally { setSavingAE(false); }
  };

  const navigate = useNavigate();
  useEffect(() => {
    if (!devis?.vente_id || devis?.statut !== 'commande') return;
    api.getBLVentes({ vente_id: devis.vente_id })
      .then(r => setBlCount((r.data?.data || r.data || []).length))
      .catch(() => setBlCount(0));
  }, [devis?.vente_id, devis?.statut]);

  // Vérifie le statut de blocage client
  useEffect(() => {
    if (devis?.entreprise_id && !devis.entreprise_id.startsWith('PER-')) {
      api.getStatutBlocageClient(devis.entreprise_id)
        .then(r => setBlocageClient(r.data))
        .catch(() => setBlocageClient(null));
    } else {
      setBlocageClient(null);
    }
  }, [devis?.entreprise_id]);

  // Charge les quantités en commande pour cette vente
  useEffect(() => {
    if (!devis?.vente_id || devis?.statut !== 'commande') return;
    api.getAchatsEnCommande({ source_type: 'vente', source_id: devis.vente_id, agence_id: devis.agence_id })
      .then(r => setEnCommandeVente(r.data || {}))
      .catch(() => setEnCommandeVente({}));
  }, [devis?.vente_id, devis?.statut]);

  // Smart buttons — toutes CAs liées (tous stades)
  useEffect(() => {
    if (!devis?.vente_id) { setVenteLinkedCAs([]); return; }
    api.getAchatsByDocument({ source_type: 'vente', source_id: devis.vente_id })
      .then(r => setVenteLinkedCAs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setVenteLinkedCAs([])  );
  }, [devis?.vente_id]);

  // Chargement des matériels disponibles pour les lignes matériel cochées sans materiel_id
  useEffect(() => {
    if (!devis?.lignes) return;
    const toLoad = selectedCAIds
      .map(lid => (devis.lignes).find(l => l.ligne_id === lid))
      .filter(l => l && l.type_article === 'materiel' && l.article_id);
    for (const l of toLoad) {
      const aid = l.article_id;
      if (materielOptions[aid] !== undefined || materielOptionsLoading[aid]) continue;
      setMaterielOptionsLoading(prev => ({ ...prev, [aid]: true }));
      api.getMaterielsEnStockParArticle(aid, devis.agence_id)
        .then(r => setMaterielOptions(prev => ({ ...prev, [aid]: r.data || [] })))
        .catch(() => setMaterielOptions(prev => ({ ...prev, [aid]: [] })))
        .finally(() => setMaterielOptionsLoading(prev => ({ ...prev, [aid]: false })));
    }
  }, [selectedCAIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Compteur documents — chargé dès l'ouverture du devis ──────────────────
  useEffect(() => {
    if (!devis?.vente_id) return;
    api.getDevisDocuments(devis.vente_id)
      .then(r => setDocCount((r.data || []).length))
      .catch(() => {});
  }, [devis?.vente_id]);

  const handleCommanderLignesVente = async () => {
    if (!devis?.vente_id || selectedCAIds.length === 0 || sendingCA) return;
    const lignes = (devis?.lignes || [])
      .filter(l => selectedCAIds.includes(l.ligne_id) && l.article_id && l.type_ligne !== 'commentaire')
      .map(l => ({
        article_id:  l.article_id,
        reference:   l.reference || '',
        designation: l.designation || '',
        quantite:    Number(l.quantite) || 1,
      }));
    if (!lignes.length) { toast.error('Aucune ligne article valide sélectionnée'); return; }
    setSendingCA(true);
    try {
      const resp = await api.getCandidatsAggregation({
        agence_id: devis.agence_id,
        lignes,
      });
      const candidats = resp.data || {};
      if (Object.keys(candidats).length > 0) {
        // Des CAs candidates existent → ouvrir la modale
        setAggregationModalVente({
          candidats,
          lignes,
          source_id: devis.vente_id,
          agence_id: devis.agence_id,
        });
        setSendingCA(false);
        return;
      }
      // Pas de candidats → créer directement
      await _doCommanderLignesVente(lignes, {}, devis.vente_id, devis.agence_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la vérification des CA');
      setSendingCA(false);
    }
  };

  const _doCommanderLignesVente = async (lignes, choix, sourceId, agenceId) => {
    setSendingCA(true);
    try {
      const r = await api.creerAchatDepuisSource({
        source_type: 'vente',
        source_id:   sourceId,
        agence_id:   agenceId,
        lignes,
        choix,
      });
      const achats = r.data?.achats || [];
      const msgs = achats.map(a => a.action === 'ajout' ? `${a.numero} (ajout)` : a.numero).join(', ');
      const allAjout = achats.length > 0 && achats.every(a => a.action === 'ajout');
      toast.success(
        allAjout
          ? `${achats.length} CA enrichie${achats.length > 1 ? 's' : ''} : ${msgs}`
          : `${achats.length} CA créée${achats.length > 1 ? 's' : ''} : ${msgs}`
      );
      pageCache.deleteByPrefix('achats-'); // invalider le cache achats après création CA
      setSelectedCAIds([]);
      setAggregationModalVente(null);
      api.getAchatsEnCommande({ source_type: 'vente', source_id: sourceId, agence_id: agenceId })
        .then(res => setEnCommandeVente(res.data || {}))
        .catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur création CA');
    } finally {
      setSendingCA(false);
    }
  };


  const creatingBLRef = useRef(false);
  const handleCreerBL = async () => {
    if (creatingBLRef.current) return;   // guard synchrone anti double-clic
    creatingBLRef.current = true;
    setCreatingBL(true);
    try {
      const r = await api.creerBLDepuisDevis(devis.vente_id, {});
      const alertes = r.data?.alertes_stock || [];
      if (alertes.length > 0) {
        const articles = alertes.map(a =>
          `${a.designation} (demandé : ${a.quantite_demandee}, dispo : ${a.quantite_disponible})`
        ).join(' | ');
        toast.warning(`BL créé — Stock insuffisant : ${articles}`, { duration: 8000 });
      } else {
        toast.success('Bon de livraison créé');
      }
      setBlCount(n => (n || 0) + 1);
      navigate(`/bons-livraison-ventes?vente_id=${devis.vente_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création BL');
    } finally {
      creatingBLRef.current = false;
      setCreatingBL(false);
    }
  };

  // ── Facturation directe (tout facturer) ─────────────────────────────────
  const handleFacturer = async () => {
    setFacturant(true);
    try {
      const r = await api.facturerBC(devis.vente_id, {});
      toast.success(`Facture ${r.data?.ext_invoice_number || ''} créée dans Pennylane`);
      if (r.data?.facture_id) { /* facture_id stored server-side */ }
      setStatutFacturation('en_attente_paiement');
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création facture Pennylane');
    } finally {
      setFacturant(false);
    }
  };

  const handleFacturerLignesSelectionnes = async () => {
    if (!selectedCAIds?.length) return;
    setFacturant(true);
    try {
      const r = await api.facturerBC(devis.vente_id, { ligne_ids: selectedCAIds });
      toast.success(`Facture ${r.data?.ext_invoice_number || ''} créée dans Pennylane`);
      setStatutFacturation('en_attente_paiement');
      setSelectedCAIds([]);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur facturation des lignes sélectionnées');
    } finally {
      setFacturant(false);
    }
  };



  const handleSupprimerDraft = async () => {
    if (!window.confirm('Supprimer définitivement cette facture brouillon et remettre à zéro les lignes facturées ?')) return;
    setSupprimantDraft(true);
    try {
      const r = await api.supprimerFactureDraft(devis.vente_id);
      toast.success('Facture brouillon supprimée — vous pouvez refacturer');
      setStatutFacturation(r.data?.statut_facturation || 'non_facturee');
      setFactureExtStatus(null);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression facture');
    } finally {
      setSupprimantDraft(false);
    }
  };

  // ── Drag & Drop sensors ──
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Charger les emplacements une seule fois pour l'affichage du stock live
  useEffect(() => {
    api.getArticlesEmplacements().then(r => setEmplacements(r.data || r || {})).catch(() => {});
    api.getStocks().then(r => setAllStocks(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  // ── État client inline ──
  // États client — gérés en interne par ClientSearchField (composant partagé)
  const [clientQuery]   = useState(''); // conservé pour compatibilité avec clientRef/useEffect
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactDropOpen, setContactDropOpen] = useState(false);
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [newContactForm, setNewContactForm] = useState({ prenom: '', nom: '', email: '', telephone_mobile: '' });
  const [savingContact, setSavingContact] = useState(false);
  const clientRef = useRef();
  const contactDropRef = useRef();

  // Interlocuteurs (multi-select)
  const [interlocuteurs, setInterlocuteurs] = useState(() => {
    if (devis?.interlocuteurs?.length) return devis.interlocuteurs;
    // Backward compat : devis existant sans interlocuteurs mais avec contact_id
    if (!isNew && devis?.contact_id && devis?.contact_nom) return [{ id: devis.contact_id, nom: devis.contact_nom, email: '' }];
    return [];
  });

  const [form, setForm] = useState(() => isNew ? {
    entreprise_id: null, entreprise_nom: '', contact_id: null, contact_nom: null,
    conditions_paiement_nom: '', mode_reglement: null,
    date_devis: today(), date_validite: validiteDefault(),
    reference_client: '', notes: '', titre: '', lignes: [],
    agence_id: activeAgences?.length === 1 ? activeAgences[0].agence_id : null,
    suivi_par: currentUser?.user_id || null,
  } : {
    entreprise_id: devis.entreprise_id, entreprise_nom: devis.entreprise_nom,
    contact_id: devis.contact_id, contact_nom: devis.contact_nom,
    conditions_paiement_nom: devis.conditions_paiement_nom || '',
    mode_reglement: devis.mode_reglement || null,
    date_devis: devis.date_devis || today(),
    date_validite: devis.date_validite || validiteDefault(),
    reference_client: devis.reference_client || '',
    notes: devis.notes || '',
    titre: devis.titre || '',
    lignes: (devis.lignes || []).map((l, i) => ({ remise: 0, ...l, ligne_id: l.ligne_id || `L-loaded-${i}-${Date.now()}` })),
    agence_id: devis.agence_id,
    suivi_par: devis.suivi_par || devis.created_by || null,
  });
  // Ref toujours à jour pour handleSave (évite la closure stale sur form)
  const formRef = useRef(null);
  formRef.current = form;

  // Conditions commerciales de l'entreprise cliente (remises par agence/type)
  const conditionsPaiementRef = useRef({});

  // ── Auto-actualisation silencieuse des conditions client à l'ouverture ────
  const autoRefreshedRef = useRef(false);
  useEffect(() => {
    const statutModifiable = devis?.statut === 'devis' || devis?.statut === 'devis_envoye';
    if (isNew || !form.entreprise_id || form.entreprise_id.startsWith('PER-') || !devis?.vente_id || !statutModifiable || autoRefreshedRef.current) return;
    autoRefreshedRef.current = true;
    (async () => {
      try {
        const [entRes, condRes, contactsRes] = await Promise.all([
          api.getEntreprise(form.entreprise_id),
          api.getConditionsPaiement(),
          api.getPersonnesEntreprise(form.entreprise_id),
        ]);
        const ent = entRes.data;
        const mapping = condRes.data || [];
        const newList = Array.isArray(contactsRes.data?.items ?? contactsRes.data)
          ? (contactsRes.data?.items ?? contactsRes.data) : [];
        setContacts(newList);
        if (interlocuteurs.length > 0) {
          setInterlocuteurs(prev => prev.map(inter => {
            const found = newList.find(c => (c.personne_id || c.contact_id) === inter.id);
            return found ? { ...inter, email: found.email || found.email_pro || inter.email } : inter;
          }));
        }
        const newEntNom = ent.nom || form.entreprise_nom;
        // Utiliser l'agence RÉELLE du devis (pas le filtre "agence active" de la nav)
        // et conserver les valeurs actuelles si aucune config n'existe pour cette agence
        let newMode = form.mode_reglement || 'comptant';
        let newCondNom = form.conditions_paiement_nom || null;
        const cpAgence = ent?.conditions_paiement_par_agence?.[devis.agence_id] || null;
        if (cpAgence) {
          const mapped = mapping.find(c => c.id_odoo === cpAgence.id_odoo);
          newMode    = cpAgence.mode_reglement ?? mapped?.mode_reglement ?? 'comptant';
          newCondNom = cpAgence.nom_condition ?? null;
        }
        const updates = { mode_reglement: newMode, conditions_paiement_nom: newCondNom };
        if (newEntNom && newEntNom !== form.entreprise_nom) updates.entreprise_nom = newEntNom;
        await api.updateVente(devis.vente_id, updates);
        setForm(f => ({ ...f, ...updates }));
      } catch { /* actualisation silencieuse : on ignore les erreurs */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, form.entreprise_id, devis?.vente_id]);

  // ── Drag & Drop handler ──
  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setForm(f => {
      const ids = f.lignes.map(l => l.ligne_id);
      const oldIdx = ids.indexOf(active.id);
      const newIdx = ids.indexOf(over.id);
      if (oldIdx < 0 || newIdx < 0) return f;
      return { ...f, lignes: arrayMove(f.lignes, oldIdx, newIdx) };
    });
  };

  // ── TVA Vente — résolution depuis le CP de l'agence du devis ──
  useEffect(() => {
    // Priorité 1 : agence du devis (form.agence_id)
    // Priorité 2 : première agence active du contexte
    // Priorité 3 : première agence de la première société (fallback org)
    let cp = '';
    if (form.agence_id && societes) {
      outer: for (const soc of societes) {
        for (const ag of (soc.agences || [])) {
          if (ag.agence_id === form.agence_id) { cp = ag.code_postal || ''; break outer; }
        }
      }
    }
    if (!cp && activeAgences?.length) {
      cp = activeAgences[0]?.code_postal || '';
    }
    if (!cp && societes?.length) {
      cp = societes[0]?.agences?.[0]?.code_postal || '';
    }
    api.resolveTva('vente', cp)
      .then(r => {
        const rates = (r.data?.taux_disponibles || []).map(t => ({
          taux: t.taux,
          designation: `${t.taux}%`,
          tva_type_id: t.tva_type_id,
        }));
        if (rates.length > 0) setTvaRates(rates);
      })
      .catch(() => {});
  }, [form.agence_id, activeAgences, societes]);

  // Recherche entreprise
  // searchClient — remplacé par ClientSearchField, conservé pour compatibilité
  const searchClient = useCallback(async () => {}, []);

  useEffect(() => {
    const t = setTimeout(() => searchClient(clientQuery), 250);
    return () => clearTimeout(t);
  }, [clientQuery, searchClient]);

  // Chargement contacts quand entreprise sélectionnée
  const refreshContacts = useCallback((entreprise_id) => {
    if (!entreprise_id || entreprise_id.startsWith('PER-')) { setContacts([]); return; }
    setContactsLoading(true);
    api.getPersonnes({ entreprise_id, per_page: 100 })
      .then(r => {
        const loadedContacts = r.data.data || r.data || [];
        setContacts(loadedContacts);
        // Compléter les emails manquants dans les interlocuteurs depuis les contacts chargés
        setInterlocuteurs(prev => prev.map(i => {
          if (i.email) return i;
          const contact = loadedContacts.find(c => (c.personne_id || c.contact_id) === i.id);
          return contact?.email ? { ...i, email: contact.email } : i;
        }));
      })
      .catch(() => setContacts([]))
      .finally(() => setContactsLoading(false));
  }, []);

  useEffect(() => {
    refreshContacts(form.entreprise_id);
  }, [form.entreprise_id, refreshContacts]);

  // Fermer dropdown client si clic dehors
  useEffect(() => {
    const h = (e) => {
      if (clientRef.current && !clientRef.current.contains(e.target)) { /* ClientSearchField gère sa propre fermeture */ }
      if (contactDropRef.current && !contactDropRef.current.contains(e.target)) setContactDropOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // ── SSE — rafraîchissement temps réel du statut de facturation ────────────
  useEffect(() => {
    if (!devis?.vente_id || statutFacturation !== 'en_attente_paiement') return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const es = new EventSource(`${base}/api/ventes/${devis.vente_id}/sse?token=${encodeURIComponent(token)}`);
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === 'status_update') {
          if (data.facture_ext_status) setFactureExtStatus(data.facture_ext_status);
          if (data.statut_facturation) {
            setStatutFacturation(data.statut_facturation);
            if (data.statut_facturation === 'validee') {
              toast.success('Facture validée — BL disponible');
            } else if (data.statut_facturation === 'payee') {
              toast.success('Facture payée — BL disponible');
            } else if (data.facture_ext_status === 'posted') {
              // comptant : ext_status mis à jour, bouton "Valider paiement" doit apparaître
              toast.info('Facture validée dans Odoo');
            }
          }
        }
      } catch {}
    };
    // Ne pas fermer sur erreur réseau — EventSource se reconnecte automatiquement
    return () => es.close();
  }, [devis?.vente_id, statutFacturation]);

  // Sélection client (entreprise ou particulier) depuis ClientSearchField
  const selectClient = async (r) => {
    // r = { client_id, client_nom, client_type, tiers_id } — format ClientSearchField
    const isParticulier = r.client_type === 'personne';
    setForm(f => ({
      ...f,
      entreprise_id: r.client_id,
      entreprise_nom: r.client_nom,
      contact_id: null, contact_nom: null,
      ...(isParticulier && { mode_reglement: 'comptant', conditions_paiement_nom: 'Paiement immédiat' }),
    }));
    setInterlocuteurs([]);

    if (isParticulier) return; // Pas de conditions par agence ni de contacts pour un particulier

    // Résoudre les conditions de paiement depuis la fiche entreprise (par agence active)
    try {
      const res = await api.getEntreprise(r.client_id);
      const fullEnt = res.data || res;
      checkSiren(fullEnt);
      conditionsPaiementRef.current = fullEnt.conditions_paiement_par_agence || {};
      let cpNom = null;
      let cpMode = null;
      for (const ag of activeAgences) {
        const cpa = fullEnt.conditions_paiement_par_agence?.[ag.agence_id];
        if (cpa?.nom_condition || cpa?.mode_reglement) {
          cpNom = cpa.nom_condition ?? cpNom;
          cpMode = cpa.mode_reglement ?? cpMode;
          break;
        }
      }
      if (!cpMode) cpMode = 'comptant';
      setForm(f => ({
        ...f,
        ...(cpNom  !== null && { conditions_paiement_nom: cpNom }),
        ...(cpMode !== null && { mode_reglement: cpMode }),
      }));
    } catch {}
  };

  const totaux = (() => {
    let ht = 0, tva = 0;
    form.lignes.forEach(l => {
      if (l.type_ligne === 'texte' || l.type_ligne === 'section') return;
      const lht = (Number(l.quantite) || 0) * (Number(l.prix_unitaire_ht) || 0) * (1 - (Number(l.remise) || 0) / 100);
      ht += lht;
      tva += lht * (Number(l.taux_tva) || 0) / 100;
    });
    return { ht: Math.round(ht * 100) / 100, tva: Math.round(tva * 100) / 100, ttc: Math.round((ht + tva) * 100) / 100 };
  })();

  const addArticle = (art) => {
    // ── Ligne Commentaire ──────────────────────────────────────────────────────
    if (art.type_ligne === 'texte') {
      setForm(f => ({
        ...f,
        lignes: [...f.lignes, {
          ligne_id: `L-${Date.now()}`,
          type_ligne: 'texte',
          designation: art.designation || '',
          quantite: 0, prix_unitaire_ht: 0, remise: 0, taux_tva: 0,
          montant_ht: 0, montant_tva: 0, montant_ttc: 0,
        }],
      }));
      return;
    }
    // ── Ligne Section ──────────────────────────────────────────────────────────
    if (art.type_ligne === 'section') {
      setForm(f => ({
        ...f,
        lignes: [...f.lignes, {
          ligne_id: `L-${Date.now()}`,
          type_ligne: 'section',
          designation: art.designation || '',
          quantite: 0, prix_unitaire_ht: 0, remise: 0, taux_tva: 0,
          montant_ht: 0, montant_tva: 0, montant_ttc: 0,
        }],
      }));
      return;
    }
    // ── Ligne Article (défaut) ─────────────────────────────────────────────────
    const agenceId = form.agence_id;
    const remise = conditionsPaiementRef.current[agenceId]?.remises?.[art.type] || 0;
    const ligne = {
      ligne_id: `L-${Date.now()}`,
      type_ligne: 'article',
      article_id: art.article_id,
      reference: art.reference || '',
      designation: art.designation || art.reference || '',
      type_article: art.type || '',
      quantite: 1,
      prix_unitaire_ht: art.prix_vente || 0,
      remise,
      taux_tva: _resolveTauxTva(art.tva_vente_type_id, tvaRates),
      montant_ht: art.prix_vente || 0,
      montant_tva: (art.prix_vente || 0) * (_resolveTauxTva(art.tva_vente_type_id, tvaRates) / 100),
      montant_ttc: (art.prix_vente || 0) * (1 + _resolveTauxTva(art.tva_vente_type_id, tvaRates) / 100),
      marque_nom: art.marque_nom || null,
      modele_nom: art.modele_nom || null,
      quantite_stock: art.quantite_stock ?? null,
      gestion_stock: art.gestion_stock ?? false,
      unite_symbole: art.unite_symbole || null,
      description_html: art.description_html || null,
      immat: null, n_serie: null,
    };
    setForm(f => ({ ...f, lignes: [...f.lignes, ligne] }));
  };

  const changeLigne = (idx, field, val) => {
    setForm(f => {
      const lignes = [...f.lignes];
      lignes[idx] = { ...lignes[idx], [field]: val };
      return { ...f, lignes };
    });
  };

  const deleteLigne = (idx) => {
    setForm(f => ({ ...f, lignes: f.lignes.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    // Forcer le blur du champ actif (ex: NumericInput quantité) pour flusher
    // les mises à jour de state avant de lire formRef.current
    flushSync(() => { document.activeElement?.blur(); });
    const f = formRef.current;
    if (!f.entreprise_nom) { toast.error('Sélectionnez un client'); return; }
    if (f.lignes.length === 0) { toast.error('Ajoutez au moins une ligne'); return; }
    setSaving(true);
    const primaryContact = interlocuteurs[0] || null;
    const payload = {
      ...f,
      interlocuteurs,
      contact_id: primaryContact?.id || f.contact_id || null,
      contact_nom: primaryContact?.nom || f.contact_nom || null,
    };
    try {
      if (isNew) {
        await api.createVente(payload);
        toast.success('Devis créé');
        setRappelBanner(null);
      } else if (statut === 'commande') {
        await api.updateVente(devis.vente_id, {
          lignes: f.lignes,
          titre: f.titre ?? null,
          reference_client: f.reference_client ?? null,
          suivi_par: f.suivi_par ?? null,
        });
        toast.success('Commande mise à jour');
        setReadOnly(true);
      } else {
        await api.updateVente(devis.vente_id, payload);
        toast.success('Devis mis à jour');
        setRappelBanner(null);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleStatut = async (statut) => {
    setChangingStatut(true);
    try {
      await api.changeStatutVente(devis.vente_id, statut);
      toast.success(`Statut mis à jour : ${STATUTS[statut]?.label}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setChangingStatut(false);
    }
  };

  const inp = `w-full text-sm px-3 py-2 rounded-lg border focus:outline-none focus:ring-1`;
  const inpSt = { borderColor: '#E6E6E1', background: '#FFFFFF', color: '#1A1A1A' };
  const statut = devis?.statut || 'devis';
  const canEdit = !devis || (statut !== 'commande' && statut !== 'annule');

  // ── Chargement des onglets ──
  const loadTab = async (tab) => {
    if (isNew || !devis?.vente_id) return;
    setLoadingTab(true);
    try {
      if (tab === 'revisions') {
        const r = await api.getDevisRevisions(devis.vente_id);
        setRevisions(r.data || []);
      }
      if (tab === 'factures') {
        setFacturesLoading(true);
        try {
          const r = await api.listerFacturesBC(devis.vente_id);
          const list = r.data || [];
          setFactures(list);
          setFacturesCount(list.length);
        } catch { setFactures([]); }
        finally { setFacturesLoading(false); }
      }
    } catch { /* silent */ }
    finally { setLoadingTab(false); }
  };

  const handleCreerBLFromFacture = async (factureId) => {
    if (creatingBLFac) return;  // guard synchrone
    setCreatingBLFac(factureId);
    try {
      const r = await api.creerBLDepuisFacture(devis.vente_id, { facture_id: factureId });
      const alertes = r.data?.alertes_stock || [];
      if (alertes.length > 0) {
        const articles = alertes.map(a => `${a.designation} (demandé : ${a.quantite_demandee}, dispo : ${a.quantite_disponible})`).join(' | ');
        toast.warning(`BL créé — Stock insuffisant : ${articles}`, { duration: 8000 });
      } else {
        toast.success('Bon de livraison créé');
      }
      setBlCount(n => (n || 0) + 1);
      navigate(`/bons-livraison-ventes?vente_id=${devis.vente_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création BL');
    } finally {
      setCreatingBLFac(null);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    loadTab(tab);
  };

  // ── Imprimer ──
  const handleImprimer = async () => {
    setEnvoyerMenuOpen(false);
    try {
      const resp = await api.getDevisPdf(devis.vente_id);
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${devis.numero || devis.vente_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (e) {
      toast.error("Erreur lors de la génération du PDF");
    }
  };

  // ── Envoyer par email ──
  const openMailModal = () => {
    setEnvoyerMenuOpen(false);
    setEmailDevisOpen(true);
  };

  const handleSendMail = async ({ emails, sujet, corps }) => {
    if (!emails.length) return;
    setSendingMail(true);
    try {
      await api.envoyerEmailDevis(devis.vente_id, { destinataires: emails, sujet, corps });
      toast.success(`Devis envoyé à ${emails.join(', ')}`);
      setEmailDevisOpen(false);
      onSaved?.({ ...devis, statut: 'devis_envoye' });
      loadTab(activeTab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur envoi email");
    } finally { setSendingMail(false); }
  };

  // ── Rappeler révision — charge les données dans le formulaire (pas de sauvegarde automatique)
  const [rappelBanner, setRappelBanner] = useState(null); // { numero_revision }
  const handleRappeler = async (revId) => {
    const rev = revisions.find(r => r.revision_id === revId);
    if (!rev) return;
    setForm(f => ({ ...f, lignes: rev.lignes || [], notes: rev.notes || '' }));
    setActiveTab('contenu');
    setRappelBanner({ numero_revision: rev.numero_revision });
    setRappelConfirm(null);
    toast.success(`Révision V${rev.numero_revision} chargée — enregistrez pour confirmer`);
    // Tracer l'activité (sans créer de nouvelle version)
    try { await api.rappelerRevision(devis.vente_id, revId); } catch { /* non bloquant */ }
  };

  const footer = (
    <div className="flex items-end justify-between gap-6">
      <div className="flex gap-6 text-sm">
        <div>
          <div className="text-xs mb-0.5" style={{ color: '#6B7280' }}>Total HT</div>
          <div className="font-semibold" style={{ color: '#1A1A1A' }}>{fmt(totaux.ht)} €</div>
        </div>
        <div>
          <div className="text-xs mb-0.5" style={{ color: '#6B7280' }}>TVA</div>
          <div className="font-semibold" style={{ color: '#1A1A1A' }}>{fmt(totaux.tva)} €</div>
        </div>
        <div>
          <div className="text-xs mb-0.5 font-bold" style={{ color: '#1A1A1A' }}>Total TTC</div>
          <div className="text-lg font-bold" style={{ color: 'var(--primary-solid)' }}>{fmt(totaux.ttc)} €</div>
        </div>
      </div>
      <div className="flex gap-2">
        {/* Remettre en devis — commande (sans BL/facture) ou annulé */}
        {!isNew && (statut === 'annule' || statut === 'commande') && (
          <button onClick={() => {
            if (statut === 'commande' && !window.confirm('Remettre ce bon de commande en statut Devis ?\nLe numéro de commande sera conservé, mais le statut repassera en brouillon éditable.')) return;
            handleStatut('devis');
          }} disabled={changingStatut}
            data-testid="vente-btn-remettre-devis"
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all hover:opacity-90 disabled:opacity-50"
            style={{ background: '#F1F5F9', color: '#475569', border: '1px solid #CBD5E1' }}>
            {changingStatut ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
            Remettre en devis
          </button>
        )}

        {/* Supprimer — brouillon uniquement */}
        {!isNew && statut === 'devis' && !readOnly && (
          <button onClick={async () => {
            if (!window.confirm('Supprimer définitivement ce devis ?')) return;
            try {
              await api.deleteVente(devis.vente_id);
              toast.success('Devis supprimé');
              onSaved?.();
            } catch { toast.error('Erreur lors de la suppression'); }
          }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all hover:opacity-90"
            style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}
            data-testid="vente-delete-btn">
            <Trash2 size={14} /> Supprimer
          </button>
        )}

        {/* Dupliquer — tout devis existant */}
        {!isNew && (
          <button onClick={() => {
            setDupliqueAgenceId(devis?.agence_id || null);
            setShowDupliqueModal(true);
          }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all hover:opacity-90"
            style={{ background: '#F1F5F9', color: '#475569', border: '1px solid #CBD5E1' }}
            data-testid="vente-dupliquer-btn">
            <Copy size={14} /> Dupliquer
          </button>
        )}

        {/* Modale choix agence pour duplication — déplacée hors du SidePanel en sibling pour éviter le FocusScope du Sheet Radix */}

        {/* Nouvelle version — devis existant non brouillon */}
        {!isNew && !readOnly && (
          <button onClick={async () => {
            if (!window.confirm('Créer une nouvelle version de ce devis ? Le contenu actuel sera archivé en révision.')) return;
            try {
              const r = await api.nouvelleVersion(devis.vente_id);
              toast.success(`Version ${r.data.devis?.numero || ''} créée`);
              onSaved?.(r.data.devis);
              loadTab('revisions');
            } catch { toast.error('Erreur lors de la création de version'); }
          }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-all hover:opacity-90"
            style={{ background: '#E0F2FE', color: '#0369A1', border: '1px solid #BAE6FD' }}
            data-testid="vente-nouvelle-version-btn">
            <GitBranch size={14} /> Nouvelle version
          </button>
        )}

        {/* Enregistrer — affiché si éditable ou si commande (titre/ref/suivi modifiables) */}
        {(!readOnly || statut === 'commande') && (
          <button onClick={handleSave} disabled={saving} data-testid="vente-save-btn"
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-50"
            style={{ background: 'var(--primary-gradient)' }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {isNew ? 'Créer le devis' : 'Enregistrer'}
          </button>
        )}
      </div>
    </div>
  );

  const headerExtra = !isNew ? (
    <div className="flex items-center gap-2">
      <StatutBadge statut={statut} />
    </div>
  ) : null;

  return (
    <>
    <SidePanel
      open={true}
      onOpenChange={(v) => { if (!v) onClose(); }}
      title={isNew ? 'Nouveau devis' : devis.numero}
      copyValue={!isNew ? devis.numero : undefined}
      subtitle="Document de vente"
      size="xl"
      footer={footer}
      headerExtra={headerExtra}
      onFocusOutside={showReglementPanel ? (e) => e.preventDefault() : undefined}
      headerCenter={
        <div className="flex items-center gap-3">
          {/* Badge blocage client */}
          {blocageClient?.bloque && (
            <button data-testid="badge-blocage-vente" onClick={() => setShowBlocageModal(true)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer transition-all hover:opacity-80"
              style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>
              <AlertTriangle size={11} /> Bloqué paiement
            </button>
          )}
          {!blocageClient?.bloque && blocageClient?.jours_retard > 0 && (
            <button data-testid="badge-retard-vente" onClick={() => setShowBlocageModal(true)}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer transition-all hover:opacity-80"
              style={{ background: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA' }}>
              <AlertTriangle size={11} /> Retard paiement
            </button>
          )}
          {venteLinkedCAs.length > 0 && (
            <SmartButton
              icon={<ShoppingCart size={13} />}
              label="Achats"
              count={venteLinkedCAs.length}
              color="blue"
              onClick={() => {
                if (venteLinkedCAs.length === 1) {
                  navigate(`/achats?open_achat=${venteLinkedCAs[0].achat_id}`);
                } else {
                  const ids = venteLinkedCAs.map(a => a.achat_id).join(',');
                  navigate(`/achats?filter_ids=${ids}&source_label=${encodeURIComponent(devis?.numero || '')}`);
                }
                onClose();
              }}
            />
          )}
        </div>
      }
    >
      <div className="space-y-6">
        {/* Workflow buttons */}
        {!isNew && statut !== 'annule' && (
          <div className="flex gap-2 flex-wrap items-center">
            {/* Bouton "Envoyer le devis" — menu dropdown */}
            {(statut === 'devis' || statut === 'devis_envoye') && (
              <div className="relative" ref={envoyerMenuRef}>
                <button
                  data-testid="vente-btn-envoyer"
                  onClick={() => setEnvoyerMenuOpen(o => !o)}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                  style={{ background: '#1D4ED8' }}>
                  <Send size={13} />
                  Envoyer le devis
                  <ChevronDown size={13} style={{ marginLeft: 2 }} />
                </button>
                {envoyerMenuOpen && (
                  <div className="absolute left-0 top-full mt-1.5 rounded-xl border bg-white shadow-xl z-50 overflow-hidden"
                    style={{ borderColor: '#E6E6E1', minWidth: 200 }}>
                    <button
                      data-testid="vente-btn-envoyer-email"
                      onClick={openMailModal}
                      className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors border-b"
                      style={{ borderColor: '#F3F4F6' }}>
                      <Mail size={14} style={{ color: '#1D4ED8' }} />
                      <div>
                        <div className="text-sm font-medium" style={{ color: '#111827' }}>Envoyer par email</div>
                        <div className="text-xs" style={{ color: '#6B7280' }}>PDF en pièce jointe</div>
                      </div>
                    </button>
                    <button
                      data-testid="vente-btn-imprimer"
                      onClick={handleImprimer}
                      className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                      <Printer size={14} style={{ color: '#6B7280' }} />
                      <div>
                        <div className="text-sm font-medium" style={{ color: '#111827' }}>Imprimer / PDF</div>
                        <div className="text-xs" style={{ color: '#6B7280' }}>Ouvre le PDF dans un nouvel onglet</div>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            )}
            {(statut === 'devis' || statut === 'devis_envoye') && (
              <SignatureCommande devis={devis} interlocuteurs={interlocuteurs} onSigned={(updated) => { onSaved?.(updated); }} onSigCancelled={(updated) => { onSaved?.(updated); }} onDocAdded={() => setDocCount(c => c + 1)} />
            )}
            {statut === 'commande' && devis?.signature?.statut === 'signe' && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold"
                style={{ background: '#F0FDF4', color: '#15803D', border: '1px solid #86EFAC' }}>
                <CheckCircle2 size={13} />
                Signé par {devis.signature.signataire_nom}
                {devis.signature.signed_at && <span className="opacity-70 ml-1">
                  le {new Date(devis.signature.signed_at).toLocaleDateString('fr-FR')}
                </span>}
              </div>
            )}
                {/* Boutons Bons de Livraison — uniquement pour les commandes */}
            {statut === 'commande' && (
              <>

                {/* ── Facturation ── */}
                {statutFacturation === 'non_facturee' && selectedCAIds.length === 0 && (
                  <button
                    data-testid="vente-btn-facturer"
                    onClick={handleFacturer}
                    disabled={facturant}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
                    style={{ background: '#7C3AED' }}>
                    {facturant ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
                    Facturer dans {nomProvider}
                  </button>
                )}

                {statutFacturation === 'en_attente_paiement' && (() => {
                  const isDraft = !facture_ext_status || facture_ext_status === 'draft';
                  return (
                    <>
                      {/* Pill statut */}
                      <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold"
                        style={{ background: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA' }}>
                        <Clock size={12} />
                        {isDraft
                          ? (isPL
                              ? 'Brouillon Pennylane créé — validez pour procéder à la livraison'
                              : (modeReglement === 'en_compte'
                                  ? 'Validez la facture pour procéder à la livraison'
                                  : 'Validez la facture dans Odoo puis validez le paiement'))
                          : 'Facture validée — en attente de paiement'}
                      </span>
                      {/* Pennylane brouillon : bouton Voir + Supprimer */}
                      {isDraft && isPL && (
                        <>
                          <button
                            data-testid="vente-btn-voir-brouillon-pl"
                            onClick={handleVoirBrouillonPennylane}
                            disabled={plDraftPanel?.loading}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
                            style={{ background: '#0EA5E9' }}>
                            {plDraftPanel?.loading ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
                            Voir le brouillon
                          </button>
                          <button
                            data-testid="vente-btn-supprimer-draft-pl"
                            onClick={handleSupprimerBrouillonPennylane}
                            disabled={supprimantDraftPL}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
                            style={{ background: '#DC2626' }}>
                            {supprimantDraftPL ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                            Supprimer brouillon
                          </button>
                        </>
                      )}
                      {/* Client comptant + facture posted → statut paiement géré par cron Pennylane */}
                      {/* Bouton supprimé — le statut est mis à jour automatiquement par le job quotidien PL */}
                      {/* Pennylane validée (posted) → aperçu */}
                      {isPL && !isDraft && (
                        <button
                          data-testid="vente-btn-voir-facture-pl"
                          onClick={handleVoirFacturePennylane}
                          disabled={plDraftPanel?.loading}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all hover:bg-sky-50 disabled:opacity-50"
                          style={{ borderColor: '#BAE6FD', color: '#0369A1' }}>
                          {plDraftPanel?.loading ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
                          Voir la facture
                        </button>
                      )}
                      {/* Odoo : bouton suppression brouillon */}
                      {isDraft && !isPL && (
                        <button
                          data-testid="vente-btn-supprimer-draft-odoo"
                          onClick={handleSupprimerDraft}
                          disabled={supprimantDraft}
                          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
                          style={{ background: '#DC2626' }}>
                          {supprimantDraft ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                          Supprimer facture brouillon
                        </button>
                      )}
                    </>
                  );
                })()}

                {statutFacturation === 'validee' && (
                  <>
                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold"
                      style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
                      <CheckCircle2 size={12} />
                      Facture validée — en compte
                    </span>
                    {bcProvider === 'pennylane' && (
                      <button
                        data-testid="vente-btn-voir-facture-pl-validee"
                        onClick={handleVoirFacturePennylane}
                        disabled={plDraftPanel?.loading}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all hover:bg-sky-50 disabled:opacity-50"
                        style={{ borderColor: '#BAE6FD', color: '#0369A1' }}>
                        {plDraftPanel?.loading ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
                        Voir la facture
                      </button>
                    )}
                  </>
                )}

                {(statutFacturation === 'payee' || statutFacturation === 'partiellement_payee') && (
                  <>
                    <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold"
                      style={{ background: '#F0FDF4', color: '#15803D', border: '1px solid #86EFAC' }}>
                      <CheckCircle2 size={12} />
                      {statutFacturation === 'payee' ? 'Facture payée' : 'Partiellement payée'}
                    </span>
                    {bcProvider === 'pennylane' && (
                      <button
                        data-testid="vente-btn-voir-facture-pl-payee"
                        onClick={handleVoirFacturePennylane}
                        disabled={plDraftPanel?.loading}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all hover:bg-sky-50 disabled:opacity-50"
                        style={{ borderColor: '#BAE6FD', color: '#0369A1' }}>
                        {plDraftPanel?.loading ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />}
                        Voir la facture
                      </button>
                    )}
                  </>
                )}

                {/* ── Logistique ── */}
                {(statutFacturation === 'payee' || statutFacturation === 'partiellement_payee' || statutFacturation === 'validee') && (
                  <button
                    data-testid="vente-btn-creer-bl"
                    onClick={handleCreerBL}
                    disabled={creatingBL || !!blocageClient?.bloque}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-50"
                    style={{ background: '#2563EB' }}>
                    {creatingBL ? <Loader2 size={13} className="animate-spin" /> : <Truck size={13} />}
                    Créer un BL
                  </button>
                )}
                {blCount > 0 && (
                  <button
                    data-testid="vente-btn-voir-bls"
                    onClick={() => navigate(`/bons-livraison-ventes?vente_id=${devis.vente_id}`)}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all hover:bg-blue-50"
                    style={{ borderColor: '#BFDBFE', color: '#1D4ED8' }}>
                    <Truck size={13} />
                    Voir BL ({blCount})
                  </button>
                )}
              </>
            )}
            <button disabled={changingStatut} onClick={() => handleStatut('annule')} data-testid="vente-btn-annuler"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all hover:bg-red-50 disabled:opacity-50"
              style={{ borderColor: '#FECACA', color: '#DC2626' }}>
              {changingStatut ? <Loader2 size={13} className="animate-spin" /> : <Ban size={13} />}
              Annuler
            </button>
          </div>
        )}

        {/* Tabs Contenu / Révisions / Activité */}
        {!isNew && (
          <div className="flex border-b" style={{ borderColor: '#E6E6E1' }}>
            {[
              { key: 'contenu',      label: 'Contenu',     icon: <FileText size={13} /> },
              { key: 'factures',     label: 'Factures',    icon: <Receipt size={13} />, count: facturesCount },
              { key: 'echeances',    label: 'Échéances',   icon: <Clock size={13} />,   count: null },
              { key: 'revisions',    label: 'Révisions',   icon: <RotateCcw size={13} /> },
              { key: 'documents',    label: 'Documents', icon: <FolderOpen size={13} />, count: docCount },
              { key: 'activites',    label: 'Activités',   icon: hasActivity ? <ActivityPulse /> : <Clock size={13} /> },
            ].map(t => (
              <button key={t.key} onClick={() => handleTabChange(t.key)}
                data-testid={`tab-${t.key}`}
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px"
                style={{
                  borderColor: activeTab === t.key ? '#1D4ED8' : 'transparent',
                  color: activeTab === t.key ? '#1D4ED8' : '#6B7280',
                }}>
                {t.icon}
                {t.label}
                {t.count > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-xs font-bold text-white"
                    style={{ background: '#1D4ED8', fontSize: 10 }}>
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* ── Tab CONTENU (formulaire existant) ──────────────────────────────── */}
        {activeTab === 'contenu' && (<>

        {/* Bannière rappel de révision */}
        {rappelBanner && (
          <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg mb-2"
            style={{ background: '#FEF3C7', border: '1px solid #FDE68A' }}>
            <span className="text-xs font-medium" style={{ color: '#92400E' }}>
              Révision V{rappelBanner.numero_revision} chargée — enregistrez pour confirmer les modifications
            </span>
            <button onClick={() => setRappelBanner(null)} className="text-xs" style={{ color: '#92400E' }}>✕</button>
          </div>
        )}

        {/* Client + Dates (grille unifiée) */}        <section>
          <h3 className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#6B7280' }}>Client & Informations</h3>
          {/* Grille 3 colonnes — Col1: Client/Contact · Col2: Réf/Conditions · Col3: Dates */}
          <div className="grid grid-cols-3 gap-x-10 gap-y-3">

            {/* ── Colonne 1 : Client & Contact ── */}
            <div className="flex flex-col gap-3">
              {/* Client (entreprise ou particulier) — composant partagé */}
              <div ref={clientRef} className="flex items-center gap-2">
                <label className="text-xs font-medium flex-shrink-0" style={{ color: '#6B7280', width: 64 }}>Client</label>
                <div className="flex-1" style={{ minWidth: 0 }}>
                  <ClientSearchField
                    compact
                    readOnly={readOnly}
                    value={form.entreprise_id ? {
                      client_id: form.entreprise_id,
                      client_type: form.entreprise_id.startsWith('PER-') ? 'personne' : 'entreprise',
                      client_nom: form.entreprise_nom,
                      tiers_id: null,
                    } : null}
                    onChange={selectClient}
                    onOpen={(c) => navigate(c.client_type === 'personne'
                      ? `/annuaire?open_per=${c.client_id}`
                      : `/annuaire?open_ent=${c.client_id}`)}
                    onClear={() => { setForm(f => ({ ...f, entreprise_id: null, entreprise_nom: '', contact_id: null, contact_nom: null, conditions_paiement_nom: '', mode_reglement: null })); setContacts([]); }}
                    placeholder="Rechercher…"
                    testId="vente-client-search"
                  />
                </div>
              </div>

              {/* Interlocuteurs (multi-select) — masqué pour les particuliers */}
              {form.entreprise_id && !form.entreprise_id.startsWith('PER-') && (
                <div className="flex items-start gap-2">
                  <label className="text-xs font-medium flex-shrink-0 mt-2" style={{ color: '#6B7280', width: 64 }}>Contacts</label>
                  <div className="flex-1 flex items-start gap-1">
                    <div className="flex-1 relative" ref={contactDropRef} data-testid="contact-select-wrapper">
                      <button
                        type="button"
                        data-testid="contact-select"
                        disabled={readOnly || contactsLoading}
                        onClick={() => { if (!contactsLoading) setContactDropOpen(o => !o); }}
                        className="w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg border text-sm text-left min-h-[30px]"
                        style={{
                          ...inpSt,
                          borderColor: contactDropOpen ? '#6366F1' : 'var(--border)',
                          outline: contactDropOpen ? '2px solid #EEF2FF' : 'none',
                          cursor: !readOnly && !contactsLoading ? 'pointer' : 'default',
                          opacity: contactsLoading ? 0.6 : 1,
                          height: 'auto',
                          gap: 6,
                        }}>
                        <span className="flex flex-wrap gap-1 flex-1">
                          {contactsLoading ? (
                            <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
                              <span className="w-3 h-3 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin inline-block" />
                              Chargement…
                            </span>
                          ) : interlocuteurs.length === 0 ? (
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                              {contacts.length === 0 ? '— Aucun contact —' : '— Sélectionner —'}
                            </span>
                          ) : interlocuteurs.map(inter => (
                            <span key={inter.id}
                              className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ background: '#EEF2FF', color: '#4338CA' }}>
                              {inter.nom}
                              {!readOnly && (
                                <span
                                  role="button"
                                  data-testid={`contact-chip-remove-${inter.id}`}
                                  onClick={e => { e.stopPropagation(); setInterlocuteurs(prev => prev.filter(i => i.id !== inter.id)); }}
                                  className="cursor-pointer hover:opacity-70 ml-0.5"
                                  style={{ fontSize: 14, lineHeight: 1, color: '#6366F1' }}>×</span>
                              )}
                            </span>
                          ))}
                        </span>
                        {!readOnly && <ChevronDown size={12} style={{ flexShrink: 0, color: 'var(--text-muted)', transform: contactDropOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />}
                      </button>
                      {contactDropOpen && contacts.length > 0 && (
                        <div className="absolute left-0 top-full mt-1 w-full rounded-xl border shadow-lg z-50 overflow-hidden"
                          style={{ borderColor: 'var(--border)', background: '#fff', maxHeight: 220, overflowY: 'auto' }}>
                          {contacts.map(c => {
                            const id = c.personne_id || c.contact_id;
                            const nom = `${c.prenom || ''} ${c.nom || ''}`.trim() || String(id);
                            const isSelected = interlocuteurs.some(i => i.id === id);
                            return (
                              <button key={id} type="button"
                                data-testid={`contact-option-${id}`}
                                onClick={() => {
                                  if (isSelected) {
                                    setInterlocuteurs(prev => prev.filter(i => i.id !== id));
                                  } else {
                                    setInterlocuteurs(prev => [...prev, { id, nom, email: c.email || '' }]);
                                  }
                                }}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-left hover:bg-indigo-50 transition-colors"
                                style={{ background: isSelected ? '#EEF2FF' : 'transparent' }}>
                                <span className="flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center"
                                  style={{ borderColor: isSelected ? '#6366F1' : 'var(--border)', background: isSelected ? '#6366F1' : 'transparent' }}>
                                  {isSelected && <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                </span>
                                <span className="flex-1 truncate" style={{ color: 'var(--text)' }}>{nom}</span>
                                {c.poste && <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>{c.poste}</span>}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {!readOnly && (
                      <button type="button" title="Créer un nouveau contact"
                        onClick={() => { setNewContactForm({ prenom: '', nom: '', email: '', telephone_mobile: '' }); setShowNewContactModal(true); }}
                        className="flex-shrink-0 flex items-center justify-center rounded border hover:bg-green-50 transition-colors"
                        style={{ width: 26, height: 26, borderColor: '#E6E6E1', color: '#16A34A', marginTop: 2 }}
                        data-testid="vente-contact-add">
                        <UserPlus size={11} />
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Formulaire inline création rapide contact */}
              {showNewContactModal && form.entreprise_id && (
                <div className="rounded-xl border p-3 mt-1" style={{ borderColor: '#D1FAE5', background: '#F0FDF4' }} data-testid="new-contact-form">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold" style={{ color: '#166534' }}>Nouveau contact</span>
                    <button type="button" onClick={() => setShowNewContactModal(false)}
                      className="rounded-full p-0.5 hover:bg-green-100"><X size={12} style={{ color: '#166534' }} /></button>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <input placeholder="Prénom" value={newContactForm.prenom}
                        onChange={e => setNewContactForm(f => ({ ...f, prenom: e.target.value }))}
                        className="flex-1 text-xs px-2 rounded-lg border bg-white focus:outline-none focus:ring-1 focus:ring-green-400"
                        style={{ height: 28, borderColor: '#BBF7D0' }} data-testid="new-contact-prenom" />
                      <input placeholder="Nom *" value={newContactForm.nom}
                        onChange={e => setNewContactForm(f => ({ ...f, nom: e.target.value }))}
                        className="flex-1 text-xs px-2 rounded-lg border bg-white focus:outline-none focus:ring-1 focus:ring-green-400"
                        style={{ height: 28, borderColor: '#BBF7D0' }} data-testid="new-contact-nom" />
                    </div>
                    <div className="flex gap-2">
                      <input placeholder="Email" value={newContactForm.email}
                        onChange={e => setNewContactForm(f => ({ ...f, email: e.target.value }))}
                        type="email"
                        className="flex-1 text-xs px-2 rounded-lg border bg-white focus:outline-none focus:ring-1 focus:ring-green-400"
                        style={{ height: 28, borderColor: '#BBF7D0' }} data-testid="new-contact-email" />
                      <input placeholder="Tél. mobile" value={newContactForm.telephone_mobile}
                        onChange={e => setNewContactForm(f => ({ ...f, telephone_mobile: e.target.value }))}
                        className="flex-1 text-xs px-2 rounded-lg border bg-white focus:outline-none focus:ring-1 focus:ring-green-400"
                        style={{ height: 28, borderColor: '#BBF7D0' }} data-testid="new-contact-tel" />
                    </div>
                    <div className="flex justify-end">
                      <button type="button" disabled={!newContactForm.nom.trim() || savingContact}
                        onClick={async () => {
                          if (!newContactForm.nom.trim()) return;
                          setSavingContact(true);
                          try {
                            const r = await api.createPersonne({
                              nom: newContactForm.nom.trim(),
                              prenom: newContactForm.prenom.trim() || null,
                              email: newContactForm.email.trim() || null,
                              telephone_mobile: newContactForm.telephone_mobile.trim() || null,
                              entreprise_id: form.entreprise_id,
                            });
                            const newContact = r.data;
                            // Rafraîchir la liste puis ajouter le nouveau contact aux interlocuteurs
                            const newList = await api.getPersonnes({ entreprise_id: form.entreprise_id, per_page: 100 });
                            setContacts(newList.data.data || newList.data || []);
                            const nom = `${newContact.prenom || ''} ${newContact.nom}`.trim();
                            setInterlocuteurs(prev => [...prev, { id: newContact.personne_id, nom, email: newContact.email || '' }]);
                            setForm(f => ({ ...f, contact_id: f.contact_id || newContact.personne_id, contact_nom: f.contact_nom || nom }));
                            setShowNewContactModal(false);
                            toast.success('Contact créé');
                          } catch {
                            toast.error('Erreur lors de la création du contact');
                          } finally {
                            setSavingContact(false);
                          }
                        }}
                        className="text-xs px-3 py-1 rounded-lg font-semibold disabled:opacity-50 flex items-center gap-1.5"
                        style={{ background: '#16A34A', color: 'white', border: 'none' }}
                        data-testid="new-contact-save">
                        {savingContact ? <Loader2 size={10} className="animate-spin" /> : <UserPlus size={10} />}
                        Créer
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Colonne 2 : Titre, Référence & Conditions ── */}
            <div className="flex flex-col gap-3">
              {/* Titre du devis */}
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium flex-shrink-0" style={{ color: '#6B7280', width: 64 }}>Titre</label>
                <input value={form.titre || ''} disabled={statut === 'annule'}
                  onChange={e => setForm(f => ({ ...f, titre: e.target.value }))}
                  placeholder="Objet du devis…"
                  data-testid="devis-titre-input"
                  className="flex-1 text-xs px-2 rounded-lg border focus:outline-none focus:ring-1"
                  style={{ ...inpSt, height: 30 }} />
              </div>

              {/* Réf. client */}
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium flex-shrink-0" style={{ color: '#6B7280', width: 64 }}>Réf. client</label>
                <input value={form.reference_client || ''} disabled={statut === 'annule'}
                  onChange={e => setForm(f => ({ ...f, reference_client: e.target.value }))}
                  placeholder="N° commande…"
                  className="flex-1 text-xs px-2 rounded-lg border focus:outline-none focus:ring-1"
                  style={{ ...inpSt, height: 30 }} />
              </div>

              {/* Conditions de paiement */}
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium flex-shrink-0" style={{ color: '#6B7280', width: 64 }}>Conditions</label>
                <div className="flex-1 flex items-center gap-1.5 rounded-lg border px-2 overflow-hidden opacity-70"
                  style={{ borderColor: '#E6E6E1', background: '#FFFFFF', height: 30 }}>
                  {form.mode_reglement && (
                    <span className="flex-shrink-0 text-xs px-1.5 rounded font-semibold flex items-center gap-0.5"
                      style={{ height: 18, ...(form.mode_reglement === 'comptant'
                        ? { background: '#F0FDF4', color: '#15803D', border: '1px solid #86EFAC' }
                        : { background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #93C5FD' }) }}>
                      <CreditCard size={9} />
                      {form.mode_reglement === 'comptant' ? 'Comptant' : 'Compte'}
                    </span>
                  )}
                  <span className="text-xs truncate" style={{ color: form.conditions_paiement_nom ? '#1A1A1A' : 'var(--text-muted)' }}>
                    {form.conditions_paiement_nom || '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* ── Colonne 3 : Dates ── */}
            <div className="flex flex-col gap-3">
              {/* Date devis */}
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium flex-shrink-0" style={{ color: '#6B7280', width: 64 }}>Date devis</label>
                <input type="date" value={form.date_devis || ''} disabled={readOnly}
                  onChange={e => setForm(f => ({ ...f, date_devis: e.target.value }))}
                  className="flex-1 text-xs px-2 rounded-lg border focus:outline-none focus:ring-1"
                  style={{ ...inpSt, height: 30 }} />
              </div>

              {/* Validité */}
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium flex-shrink-0" style={{ color: '#6B7280', width: 64 }}>Validité</label>
                <input type="date" value={form.date_validite || ''} disabled={readOnly}
                  onChange={e => setForm(f => ({ ...f, date_validite: e.target.value }))}
                  className="flex-1 text-xs px-2 rounded-lg border focus:outline-none focus:ring-1"
                  style={{ ...inpSt, height: 30 }} />
              </div>

              {/* Suivi par */}
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium flex-shrink-0" style={{ color: '#6B7280', width: 64 }}>Suivi par</label>
                <select
                  value={form.suivi_par || ''}
                  disabled={statut === 'annule'}
                  onChange={e => setForm(f => ({ ...f, suivi_par: e.target.value || null }))}
                  data-testid="vente-suivi-par-select"
                  className="flex-1 text-xs px-2 rounded-lg border focus:outline-none focus:ring-1"
                  style={{ ...inpSt, height: 30 }}>
                  <option value="">— Non assigné —</option>
                  {orgUsers.map(u => (
                    <option key={u.user_id} value={u.user_id}>{u.name}</option>
                  ))}
                </select>
              </div>
            </div>

          </div>
        </section>

        {/* Lignes */}
        <section>
          <h3 className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#6B7280' }}>
            Articles ({form.lignes.length})
          </h3>
          <div className="rounded-xl border" style={{ borderColor: '#E6E6E1' }}>
            {/* Barre d'action : Commander + Facturer */}
            {readOnly && devis?.statut === 'commande' && selectedCAIds.length > 0 && (
              <div className="px-3 py-2 flex items-center gap-2 border-b" style={{ borderColor: '#E6E6E1' }}>
                <button type="button"
                  data-testid="commander-lignes-vente-btn"
                  onClick={handleCommanderLignesVente}
                  disabled={sendingCA || facturant}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-semibold text-white disabled:opacity-50"
                  style={{ background: '#1D4ED8' }}>
                  {sendingCA
                    ? <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                    : <span>Commander ({selectedCAIds.length})</span>}
                </button>
                {statutFacturation !== 'payee' && statutFacturation !== 'validee' && (
                  <button type="button"
                    data-testid="facturer-lignes-vente-btn"
                    onClick={handleFacturerLignesSelectionnes}
                    disabled={facturant || sendingCA}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-semibold text-white disabled:opacity-50"
                    style={{ background: '#7C3AED' }}>
                    {facturant
                      ? <div className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                      : <span>Facturer ({selectedCAIds.length})</span>}
                  </button>
                )}
                <button type="button" onClick={() => setSelectedCAIds([])}
                  className="text-xs px-2 py-1 rounded-lg" style={{ color: '#6B7280' }}>
                  Annuler
                </button>
              </div>
            )}
            {/* ── Section Acheteur / Exploitant ── */}
            {devis && (devis.lignes || []).some(l => l.type_article === 'materiel') && (
              <div style={{ border: '1px solid #E0E7FF', borderRadius: 10, background: '#F5F3FF', marginBottom: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px' }}>
                  <UserCheck size={14} style={{ color: '#6D28D9' }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#6D28D9', flex: 1 }}>Facturation matériel — Acheteur / Exploitant</span>
                  {savingAE && <Loader2 size={12} className="animate-spin" style={{ color: '#7C3AED' }} />}
                </div>
                <div style={{ padding: '0 14px 14px', display: 'flex', gap: 16, flexWrap: 'wrap' }}>

                  {/* Acheteur */}
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <label style={{ fontSize: 11, color: '#6D28D9', fontWeight: 700, display: 'block', marginBottom: 4 }}>
                      Acheteur facturé <span style={{ fontWeight: 400, color: '#7C3AED' }}>(laisser vide = client du devis)</span>
                    </label>
                    {acheteurNom ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#EDE9FE', borderRadius: 6, padding: '6px 10px' }}>
                        <Building2 size={13} style={{ color: '#6D28D9' }} />
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#4C1D95' }}>{acheteurNom}</span>
                        <button onClick={() => { setAcheteurId(''); setAcheteurNom(''); setRechAcheteur(''); saveAcheteurExploitant({ acheteur_id: null, acheteur_nom: null, exploitant_bl_id: exploitantBLId || null, exploitant_bl_type: exploitantBLType || null, exploitant_bl_nom: exploitantBLNom || null }); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7C3AED', padding: 2 }} data-testid="clear-acheteur"><X size={13} /></button>
                      </div>
                    ) : (
                      <div style={{ position: 'relative' }}>
                        <input data-testid="search-acheteur-bc" value={rechAcheteur} onChange={e => setRechAcheteur(e.target.value)}
                          placeholder="Rechercher une entreprise…"
                          style={{ width: '100%', padding: '6px 10px', border: '1px solid #C4B5FD', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }} />
                        {loadingAch && <Loader2 size={12} className="animate-spin" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#7C3AED' }} />}
                        {resAcheteur.length > 0 && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#fff', border: '1px solid #C4B5FD', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,.1)', maxHeight: 160, overflowY: 'auto' }}>
                            {resAcheteur.map(e => (
                              <div key={e.entreprise_id} onClick={() => { setAcheteurId(e.entreprise_id); setAcheteurNom(e.nom); setRechAcheteur(''); setResAcheteur([]); saveAcheteurExploitant({ acheteur_id: e.entreprise_id, acheteur_nom: e.nom, exploitant_bl_id: exploitantBLId || null, exploitant_bl_type: exploitantBLType || null, exploitant_bl_nom: exploitantBLNom || null }); }}
                                style={{ padding: '7px 12px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid #F3F0FF' }}
                                onMouseEnter={ev => ev.currentTarget.style.background = '#F3F0FF'} onMouseLeave={ev => ev.currentTarget.style.background = ''}>
                                {e.nom}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Exploitant */}
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <label style={{ fontSize: 11, color: '#6D28D9', fontWeight: 700, display: 'block', marginBottom: 4 }}>
                      Exploitant / Locataire <span style={{ fontWeight: 400, color: '#7C3AED' }}>(utilisateur réel du matériel)</span>
                    </label>
                    {exploitantBLNom ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#EDE9FE', borderRadius: 6, padding: '6px 10px' }}>
                        {exploitantBLType === 'personne' ? <User size={13} style={{ color: '#6D28D9' }} /> : <Building2 size={13} style={{ color: '#6D28D9' }} />}
                        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#4C1D95' }}>{exploitantBLNom}</span>
                        <span style={{ fontSize: 10, color: '#7C3AED', background: '#DDD6FE', borderRadius: 4, padding: '1px 6px' }}>{exploitantBLType === 'personne' ? 'Personne' : 'Entreprise'}</span>
                        <button onClick={() => { setExploitantBLId(''); setExploitantBLNom(''); setRechExploitant(''); saveAcheteurExploitant({ acheteur_id: acheteurId || null, acheteur_nom: acheteurNom || null, exploitant_bl_id: null, exploitant_bl_type: null, exploitant_bl_nom: null }); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7C3AED', padding: 2 }} data-testid="clear-exploitant"><X size={13} /></button>
                      </div>
                    ) : (
                      <div style={{ position: 'relative' }}>
                        <input data-testid="search-exploitant-bc" value={rechExploitant} onChange={e => setRechExploitant(e.target.value)}
                          placeholder="Rechercher entreprise ou personne…"
                          style={{ width: '100%', padding: '6px 10px', border: '1px solid #C4B5FD', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' }} />
                        {loadingExpl && <Loader2 size={12} className="animate-spin" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#7C3AED' }} />}
                        {resExploitant.length > 0 && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#fff', border: '1px solid #C4B5FD', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,.1)', maxHeight: 180, overflowY: 'auto' }}>
                            {resExploitant.map(e => (
                              <div key={e.entreprise_id || e.personne_id} onClick={() => { setExploitantBLId(e.entreprise_id || e.personne_id); setExploitantBLType(e._type); setExploitantBLNom(e._nom); setRechExploitant(''); setResExploitant([]); saveAcheteurExploitant({ acheteur_id: acheteurId || null, acheteur_nom: acheteurNom || null, exploitant_bl_id: e.entreprise_id || e.personne_id, exploitant_bl_type: e._type, exploitant_bl_nom: e._nom }); }}
                                style={{ padding: '7px 12px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid #F3F0FF', display: 'flex', alignItems: 'center', gap: 6 }}
                                onMouseEnter={ev => ev.currentTarget.style.background = '#F3F0FF'} onMouseLeave={ev => ev.currentTarget.style.background = ''}>
                                {e._type === 'personne' ? <User size={10} style={{ color: '#7C3AED' }} /> : <Building2 size={10} style={{ color: '#7C3AED' }} />}
                                {e._nom}
                                <span style={{ fontSize: 10, color: '#94A3B8', marginLeft: 'auto' }}>{e._type === 'personne' ? 'Personne' : 'Entreprise'}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ background: '#FAFAF8', borderBottom: '1px solid #E6E6E1' }}>
                    {/* Colonne checkbox CA (commande) OU drag handle (edit) */}
                    {readOnly && devis?.statut === 'commande' ? (
                      <th style={{ width: 30 }}>
                        <input type="checkbox"
                          data-testid="select-all-ca-vente"
                          className="cursor-pointer accent-blue-600"
                          checked={selectedCAIds.length > 0 && selectedCAIds.length === (form.lignes.filter(l => l.article_id && l.type_ligne !== 'commentaire' && l.type_ligne !== 'section').length)}
                          onChange={e => {
                            if (e.target.checked)
                              setSelectedCAIds(form.lignes.filter(l => l.article_id && l.type_ligne !== 'commentaire' && l.type_ligne !== 'section').map(l => l.ligne_id));
                            else setSelectedCAIds([]);
                          }}
                        />
                      </th>
                    ) : !readOnly ? (
                      <th style={{ width: 30 }} />
                    ) : null}

                    <th className="px-2 py-2 text-xs font-semibold uppercase text-left" style={{ color: '#6B7280', width: 156 }}>Référence</th>
                    <th className="px-2 py-2 text-xs font-semibold uppercase text-left" style={{ color: '#6B7280' }}>Désignation</th>
                    <th className="px-2 py-2 text-xs font-semibold uppercase text-center" style={{ color: '#6B7280', width: 70 }}>Qté</th>
                    {devis?.statut === 'commande' && (
                      <th className="px-2 py-2 text-xs font-semibold uppercase text-center" style={{ color: '#6B7280', width: 110 }}>En stock</th>
                    )}
                    {devis?.statut === 'commande' && (
                      <th className="px-2 py-2 text-xs font-semibold uppercase text-center" style={{ color: '#6B7280', width: 90 }}>Commande</th>
                    )}
                    {devis?.statut !== 'commande' && (
                      <th className="px-2 py-2 text-xs font-semibold uppercase text-center" style={{ color: '#6B7280', width: 110 }}>Stock</th>
                    )}
                    <th className="px-2 py-2 text-xs font-semibold uppercase text-center" style={{ color: '#6B7280', width: 100 }}>P.U. HT</th>
                    <th className="px-2 py-2 text-xs font-semibold uppercase text-center" style={{ color: '#6B7280', width: 75 }}>Remise</th>
                    <th className="px-2 py-2 text-xs font-semibold uppercase text-center" style={{ color: '#6B7280', width: 90 }}>P.U. Net</th>
                    <th className="px-2 py-2 text-xs font-semibold uppercase text-center" style={{ color: '#6B7280', width: 80 }}>TVA</th>
                    <th className="px-2 py-2 text-xs font-semibold uppercase text-right" style={{ color: '#6B7280', width: 100 }}>Total HT</th>
                    {!readOnly && <th style={{ width: 36 }} />}
                  </tr>
                </thead>
                <tbody>
                  {form.lignes.length === 0 && readOnly ? (
                    <tr><td colSpan={9} className="px-4 py-8 text-center text-sm" style={{ color: '#6B7280' }}>
                      Aucune ligne
                    </td></tr>
                  ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext items={form.lignes.map(l => l.ligne_id)} strategy={verticalListSortingStrategy}>
                        {form.lignes.map((l, i) => {
                          const showCACol = devis?.statut === 'commande';
                          const isArticleLine = l.article_id && l.type_ligne !== 'commentaire' && l.type_ligne !== 'section';
                          const qFact = Number(l.quantite_facturee) || 0;
                          const qCmd  = Number(l.quantite) || 0;
                          const isFullyBilled    = qFact >= qCmd && qCmd > 0;
                          const isPartiallyBilled = qFact > 0 && !isFullyBilled;
                          const checkCell = showCACol ? (
                            <td className="px-2 py-2 text-center align-middle" style={{ width: 36 }}>
                              {isArticleLine ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                  <input type="checkbox"
                                    data-testid={`ca-checkbox-${l.ligne_id}`}
                                    className="cursor-pointer accent-blue-600"
                                    disabled={isFullyBilled}
                                    style={{ opacity: isFullyBilled ? 0.35 : 1 }}
                                    checked={!isFullyBilled && selectedCAIds.includes(l.ligne_id)}
                                    onChange={e => setSelectedCAIds(s => e.target.checked ? [...s, l.ligne_id] : s.filter(x => x !== l.ligne_id))}
                                  />
                                  {isFullyBilled && (
                                    <span style={{ fontSize: 8, color: '#9CA3AF', whiteSpace: 'nowrap', lineHeight: 1 }}>Fact.</span>
                                  )}
                                  {isPartiallyBilled && (
                                    <span style={{ fontSize: 8, color: '#F59E0B', whiteSpace: 'nowrap', lineHeight: 1 }}>{qFact}/{qCmd}</span>
                                  )}
                                </div>
                              ) : null}
                            </td>
                          ) : null;
                          const enCommandeData = enCommandeVente[l.article_id];
                          const enCommandeCell = showCACol ? (
                            <td className="px-2 py-2 text-center align-middle" style={{ width: 90 }}>
                              <VenteEnCommandeCell data={enCommandeData}
                                open={caPopover === l.ligne_id}
                                onToggle={() => setCAPopover(p => p === l.ligne_id ? null : l.ligne_id)} />
                            </td>
                          ) : undefined;
                          const needsMatSelection = readOnly
                            && devis?.statut === 'commande'
                            && selectedCAIds.includes(l.ligne_id)
                            && l.type_article === 'materiel';
                          return (
                            <React.Fragment key={l.ligne_id}>
                              <LigneRow id={l.ligne_id} ligne={l} idx={i}
                                onChange={changeLigne} onDelete={deleteLigne} readOnly={readOnly}
                                emplacements={emplacements} activeAgences={activeAgences}
                                tvaRates={tvaRates}
                                checkCell={checkCell}
                                enCommandeCell={enCommandeCell}
                                uniteDecimals={uniteDecimals}
                                agenceId={devis?.agence_id || null}
                                onStockClick={({ ligne: l2, emplacements: empl, rect }) =>
                                  setStockPopover(p => p?.ligne?.ligne_id === l2.ligne_id ? null : { ligne: l2, emplacements: empl, rect })
                                } />
                              {needsMatSelection && (
                                <tr style={{ background: '#FFFBEB' }}>
                                  <td colSpan={showCACol ? 12 : 11} style={{ padding: '4px 12px 6px 44px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <span style={{ fontSize: 11, color: '#92400E', fontWeight: 600 }}>N° de série requis :</span>
                                      {materielOptionsLoading[l.article_id] ? (
                                        <span style={{ fontSize: 11, color: '#6B7280' }}>Chargement…</span>
                                      ) : (
                                        <select
                                          value={partialMaterielSelection[l.ligne_id] || l.materiel_id || ''}
                                          onChange={e => setPartialMaterielSelection(p => ({ ...p, [l.ligne_id]: e.target.value }))}
                                          style={{ fontSize: 11, padding: '2px 6px', borderRadius: 5, border: '1px solid #F59E0B', background: '#fff', cursor: 'pointer' }}
                                        >
                                          <option value="">— Sélectionner —</option>
                                          {(materielOptions[l.article_id] || []).map(m => (
                                            <option key={m.materiel_id} value={m.materiel_id}>
                                              {m.numero_serie || m.materiel_id}{m.annee ? ` (${m.annee})` : ''}
                                            </option>
                                          ))}
                                          {(materielOptions[l.article_id] || []).length === 0 && (
                                            <option disabled>Aucun matériel en stock</option>
                                          )}
                                        </select>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </SortableContext>
                    </DndContext>
                  )}
                  {!readOnly && <GhostRow onSelect={addArticle} colSpan={11} emplacements={emplacements} activeAgences={activeAgences} />}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Notes */}
        <section>
          <h3 className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: '#6B7280' }}>Notes / Conditions</h3>
          <textarea value={form.notes || ''} disabled={readOnly}
            onChange={e => { setForm(f => ({ ...f, notes: e.target.value })); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
            onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
            ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
            placeholder="Conditions générales, mentions spéciales…"
            className="w-full text-sm px-3 py-2 rounded-lg border focus:outline-none resize-none overflow-hidden"
            style={{ borderColor: '#E6E6E1', background: '#FFFFFF', color: '#1A1A1A', minHeight: '72px' }} />
        </section>
        </>)}

        {/* ── Tab FACTURES ──────────────────────────────────────────────────── */}
        {activeTab === 'factures' && (
          <div className="space-y-4">
            {facturesLoading && (
              <div className="flex items-center gap-2 text-sm py-6" style={{ color: '#6B7280' }}>
                <Loader2 size={14} className="animate-spin" /> Chargement…
              </div>
            )}
            {!facturesLoading && factures.length === 0 && (
              <div className="py-10 text-center">
                <Receipt size={28} className="mx-auto mb-3" style={{ color: '#D1D5DB' }} />
                <p className="text-sm" style={{ color: '#9CA3AF' }}>Aucune facture pour ce bon de commande.</p>
              </div>
            )}
            {factures.map((fac, fi) => {
              const statusMap = { en_attente: { label: 'En attente', bg: '#FEF3C7', color: '#92400E' }, validee: { label: 'Validée', bg: '#D1FAE5', color: '#065F46' }, payee: { label: 'Payée', bg: '#D1FAE5', color: '#065F46' }, partiellement_payee: { label: 'Part. payée', bg: '#EDE9FE', color: '#5B21B6' } };
              const s = statusMap[fac.statut_paiement] || { label: fac.statut_paiement, bg: '#F3F4F6', color: '#374151' };
              const ligs = fac.lignes || [];
              const totalHT = ligs.reduce((acc, l) => acc + (Number(l.montant_ht) || 0), 0);
              return (
                <div key={fac.facture_id} data-testid={`facture-card-${fi}`}
                  className="rounded-xl border p-4 space-y-3" style={{ borderColor: '#E6E6E1' }}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold" style={{ color: '#111827' }}>
                          {fac.ext_invoice_number || `Facture #${fi + 1}`}
                        </span>
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: s.bg, color: s.color }}>
                          {s.label}
                        </span>
                      </div>
                      <div className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
                        {fac.date_facture ? new Date(fac.date_facture).toLocaleDateString('fr-FR') : '—'}
                        {' · '}Total HT : <strong>{fmt(totalHT || fac.montant_ht)} €</strong>
                      </div>
                    </div>
                    <button
                      data-testid={`btn-creer-bl-facture-${fac.facture_id}`}
                      onClick={() => handleCreerBLFromFacture(fac.facture_id)}
                      disabled={creatingBLFac === fac.facture_id}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-opacity disabled:opacity-50"
                      style={{ background: '#1D4ED8' }}>
                      {creatingBLFac === fac.facture_id ? <Loader2 size={11} className="animate-spin" /> : <Truck size={11} />}
                      Générer BL
                    </button>
                  </div>
                  {ligs.length > 0 && (
                    <div className="rounded-lg overflow-hidden" style={{ border: '1px solid #E6E6E1' }}>
                      <table className="w-full text-xs">
                        <thead><tr style={{ background: '#F9FAFB' }}>
                          <th className="text-left px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Article</th>
                          <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Qté</th>
                          <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>PU HT</th>
                          <th className="text-right px-3 py-2 font-medium" style={{ color: '#6B7280' }}>Montant HT</th>
                        </tr></thead>
                        <tbody>
                          {ligs.map((l, li) => (
                            <tr key={li} style={{ borderTop: '1px solid #F3F4F6' }}>
                              <td className="px-3 py-2" style={{ color: '#111827' }}>
                                {l.reference && <span className="font-mono text-xs mr-1.5" style={{ color: '#6B7280' }}>{l.reference}</span>}
                                {l.designation}
                              </td>
                              <td className="px-3 py-2 text-right" style={{ color: '#374151' }}>{l.quantite}</td>
                              <td className="px-3 py-2 text-right" style={{ color: '#374151' }}>{fmt(l.prix_unitaire_ht)} €</td>
                              <td className="px-3 py-2 text-right font-medium" style={{ color: '#111827' }}>{fmt(l.montant_ht)} €</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Tab ÉCHÉANCES ─────────────────────────────────────────────────── */}
        {activeTab === 'echeances' && devis?.vente_id && (
          <EcheancesTab
            docId={devis.vente_id}
            totalTTC={devis?.total_ttc}
            fetchFn={api.getVenteEcheances}
            createFn={api.createVenteEcheance}
            updateFn={api.updateVenteEcheance}
            deleteFn={api.deleteVenteEcheance}
            readOnly={devis?.statut === 'annule'}
          />
        )}

        {/* ── Tab RÉVISIONS ─────────────────────────────────────────────────── */}
        {activeTab === 'revisions' && (
          <div className="space-y-3">
            {loadingTab && <div className="flex items-center gap-2 text-sm py-6" style={{ color: '#6B7280' }}><Loader2 size={14} className="animate-spin" /> Chargement…</div>}
            {!loadingTab && revisions.length === 0 && (
              <div className="py-10 text-center">
                <RotateCcw size={28} className="mx-auto mb-3" style={{ color: '#D1D5DB' }} />
                <p className="text-sm" style={{ color: '#9CA3AF' }}>Aucune révision enregistrée.</p>
                <p className="text-xs mt-1" style={{ color: '#D1D5DB' }}>Les révisions sont créées automatiquement lors de l'envoi ou de la modification après envoi.</p>
              </div>
            )}
            {revisions.map(rev => (
              <div key={rev.revision_id} className="rounded-xl border p-4 flex items-start justify-between gap-4"
                style={{ borderColor: '#E6E6E1' }}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold" style={{ color: '#111827' }}>{rev.label || `Révision ${rev.numero_revision}`}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ background: '#EEF2FF', color: '#4F46E5' }}>v{rev.numero_revision}</span>
                  </div>
                  <div className="text-xs" style={{ color: '#6B7280' }}>
                    Par {rev.created_by_nom || '—'} · {_fmtDatetime(rev.created_at)}
                  </div>
                  <div className="flex gap-3 mt-2 text-xs" style={{ color: '#374151' }}>
                    <span>HT : <strong>{fmt(rev.total_ht)} €</strong></span>
                    <span>TVA : {fmt(rev.total_tva)} €</span>
                    <span>TTC : <strong>{fmt(rev.total_ttc)} €</strong></span>
                  </div>
                </div>
                <div>
                  {rappelConfirm === rev.revision_id ? (
                    <div className="flex gap-2">
                      <button onClick={() => handleRappeler(rev.revision_id)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
                        style={{ background: '#1D4ED8' }}>Confirmer</button>
                      <button onClick={() => setRappelConfirm(null)}
                        className="px-3 py-1.5 rounded-lg text-xs border" style={{ borderColor: '#E6E6E1' }}>Annuler</button>
                    </div>
                  ) : (
                    <button onClick={() => setRappelConfirm(rev.revision_id)} data-testid={`rappeler-${rev.revision_id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border hover:bg-blue-50 transition-colors"
                      style={{ borderColor: '#BFDBFE', color: '#1D4ED8' }}>
                      <RotateCcw size={11} />Rappeler
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Tab ACTIVITÉ ──────────────────────────────────────────────────── */}
        {/* ── Tab DOCUMENTS ─────────────────────────────────────────────────── */}
        {activeTab === 'documents' && devis && (
          <div style={{ padding: '12px 0', height: '100%' }}>
            <DocumentsTab
              entityId={devis.vente_id}
              listFn={() => api.getDevisDocuments(devis.vente_id)}
              uploadFn={(fd) => api.uploadDevisDocument(devis.vente_id, fd)}
              updateFn={(docId, data) => api.patchDevisDocument(devis.vente_id, docId, data)}
              deleteFn={(docId) => api.deleteDevisDocument(devis.vente_id, docId)}
              downloadFn={(docId) => api.downloadDevisDocument(devis.vente_id, docId)}
              onCountChange={setDocCount}
            />
          </div>
        )}

        {/* ── Tab ACTIVITÉS ─────────────────────────────────────────────────── */}
        {activeTab === 'activites' && devis?.vente_id && (
          <div style={{ padding: '12px 0' }}>
            <ActivitesFichePanel
              record_type="vente"
              record_id={devis.vente_id}
              record_label={devis.numero}
              type_document="vente"
              numero_document={devis.numero}
              activiteFetcher={() => api.getDevisActivite(devis.vente_id).then(r => r.data || [])}
              created_by_nom={devis.created_by_nom ?? null}
            />
          </div>
        )}

      </div>

      {/* ── Stock Popover ────────────────────────────────────────────────────── */}
      {stockPopover && (
        <StockPopover
          article={{ designation: stockPopover.ligne.designation, reference: stockPopover.ligne.reference }}
          emplacements={stockPopover.emplacements}
          rect={stockPopover.rect}
          onClose={() => setStockPopover(null)}
          onTransfert={({ article, stock }) => {
            setTransfertInit({ article, stock });
            setStockPopover(null);
          }}
        />
      )}

      {/* ── Modal transfert de stock ──────────────────────────────────────────── */}
      {transfertInit && (
        <TransfertModal
          article={transfertInit.article}
          stockSource={transfertInit.stock}
          stocks={allStocks}
          onClose={() => setTransfertInit(null)}
          onSubmit={async (form) => {
            await api.creerDemandeTransfert({
              article_id: transfertInit.article.article_id,
              article_nom: transfertInit.article.designation,
              stock_source_id: transfertInit.stock.stock_id,
              agence_source_nom: transfertInit.stock.agence,
              stock_dest_id: form.stock_dest_id || null,
              agence_dest_nom: allStocks.find(s => s.stock_id === form.stock_dest_id)?.agence_nom,
              quantite: form.quantite,
              commentaire: form.commentaire,
            });
            toast.success('Demande de transfert envoyée');
            setTransfertInit(null);
          }}
        />
      )}

      {/* ── EmailComposeModal Devis ─────────────────────────────────────────── */}
      <EmailComposeModal
        open={emailDevisOpen}
        onClose={() => setEmailDevisOpen(false)}
        onSend={handleSendMail}
        destinataires={(() => {
          const chips = interlocuteurs.filter(i => i.email).map(i => ({ email: i.email, nom: i.nom || i.email, source: 'document' }));
          if (chips.length === 0) {
            const dest = devis?.contact_email || devis?.entreprise_email || '';
            const nom  = devis?.contact_nom   || devis?.interlocuteur_nom || dest;
            if (dest) return [{ email: dest, nom, source: 'document' }];
          }
          return chips;
        })()}
        entrepriseId={devis?.entreprise_id || null}
        agenceId={devis?.agence_id || null}
        typeCode="devis"
        templateVariables={{
          nom_client:    devis?.entreprise_nom || devis?.contact_nom || '',
          numero_devis:  devis?.numero || '',
          date_devis:    devis?.date_devis ? new Date(devis.date_devis).toLocaleDateString('fr-FR') : '',
          total_ttc:     devis?.total_ttc ? `${fmt(devis.total_ttc)} €` : '',
          nom_emetteur:  currentUser ? (`${currentUser.prenom || ''} ${currentUser.nom || ''}`.trim() || currentUser.name || currentUser.email || '') : '',
        }}
        titre="Envoyer le devis par email"
        documentRef={devis?.numero || ''}
        infoNote="Le PDF du devis sera automatiquement ajouté en pièce jointe."
        sending={sendingMail}
        creatable={true}
      />

    </SidePanel>

      {/* ── Side-panel brouillon Pennylane (PDF viewer + validation) ── */}
      {plDraftPanel !== null && createPortal(
        <div
          data-radix-portal=""
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.45)',
            pointerEvents: 'auto',
          }}
          onPointerDown={e => e.stopPropagation()}
          onClick={e => { e.stopPropagation(); if (e.target === e.currentTarget) closePlDraftPanel(); }}
        >
          <div
            style={{
              position: 'fixed', top: 0, right: 0,
              width: 'min(1360px, 92vw)', height: '100vh',
              background: '#ffffff', boxShadow: '-4px 0 32px rgba(0,0,0,0.18)',
              display: 'flex', flexDirection: 'column',
              pointerEvents: 'auto',
            }}
            onPointerDown={e => e.stopPropagation()}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 24px', borderBottom: '1px solid #E5E7EB', flexShrink: 0,
            }}>
              <div>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', margin: 0 }}>Brouillon Pennylane</h2>
                <p style={{ fontSize: 12, color: '#6B7280', margin: '2px 0 0 0' }}>
                  Vérifiez la facture puis validez pour générer le bon de livraison
                </p>
              </div>
              <button
                onClick={closePlDraftPanel}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6B7280' }}>
                <X size={18} />
              </button>
            </div>

            {/* Corps — PDF ou spinner */}
            <div style={{ flex: 1, overflow: 'hidden', background: '#F9FAFB' }}>
              {plDraftPanel.loading ? (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', height: '100%', gap: 12,
                }}>
                  <Loader2 size={32} className="animate-spin" style={{ color: '#0EA5E9' }} />
                  <span style={{ fontSize: 13, color: '#6B7280' }}>Chargement du PDF…</span>
                </div>
              ) : plDraftPanel.pdf_blob_url ? (
                <iframe
                  src={plDraftPanel.pdf_blob_url}
                  title="Brouillon facture Pennylane"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              ) : (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  justifyContent: 'center', height: '100%', gap: 12, padding: 32,
                }}>
                  <FileText size={40} style={{ color: '#D1D5DB' }} />
                  <p style={{ fontSize: 13, color: '#6B7280', textAlign: 'center', margin: 0 }}>
                    PDF non disponible pour le moment.<br />Vous pouvez quand même valider la facture.
                  </p>
                </div>
              )}
            </div>

            {/* Footer — actions */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 24px', borderTop: '1px solid #E5E7EB', gap: 10, flexShrink: 0,
            }}>
              {/* Supprimer brouillon — masqué si facture finalisée */}
              {facture_ext_status !== 'posted' ? (
                <button
                  data-testid="pl-draft-btn-supprimer"
                  onClick={handleSupprimerBrouillonPennylane}
                  disabled={supprimantDraftPL || validantPL}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    background: 'none', border: '1px solid #FECACA', color: '#DC2626',
                    cursor: 'pointer', opacity: (supprimantDraftPL || validantPL) ? 0.5 : 1,
                  }}>
                  {supprimantDraftPL ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                  Supprimer brouillon
                </button>
              ) : <span />}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={closePlDraftPanel}
                  style={{
                    padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    background: 'none', border: '1px solid #E5E7EB', color: '#374151', cursor: 'pointer',
                  }}>
                  Fermer
                </button>
                {/* Bouton "Saisir / Voir le règlement" */}
                <button
                  data-testid="pl-draft-btn-reglement"
                  onClick={() => setShowReglementPanel(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                    background: reglementSaisi ? 'rgba(34,197,94,0.1)' : 'none',
                    border: reglementSaisi ? '1px solid rgba(34,197,94,0.5)' : '1px solid #E5E7EB',
                    color: reglementSaisi ? '#16a34a' : '#374151', cursor: 'pointer',
                  }}>
                  <CreditCard size={13} />
                  {reglementSaisi
                    ? (facture_ext_status === 'posted' ? 'Voir le règlement' : 'Règlement saisi ✓')
                    : 'Saisir le règlement'}
                </button>
                {/* Valider la facture — masqué si facture finalisée ou si comptant sans règlement */}
                {facture_ext_status !== 'posted' && (devis?.mode_reglement !== 'comptant' || reglementSaisi) && (
                  <button
                    data-testid="pl-draft-btn-valider"
                    onClick={handleValiderPennylane}
                    disabled={validantPL || supprimantDraftPL}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700,
                      background: '#0EA5E9', color: '#ffffff', border: 'none',
                      cursor: 'pointer', opacity: (validantPL || supprimantDraftPL) ? 0.5 : 1,
                    }}>
                    {validantPL ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
                    Valider la facture
                  </button>
                )}
                {/* Facture acquittée — visible si finalisée + entièrement payée (parité LocationsV2) */}
                {facture_ext_status === 'posted' &&
                 (factures[0]?.statut_paiement === 'payee' ||
                  (reglementSaisi?.restant !== undefined && reglementSaisi.restant <= 0 &&
                   reglementSaisi.reglements?.length > 0)) && (
                  <button
                    data-testid="pl-draft-btn-acquittee"
                    onClick={handleAcquitteeVentes}
                    disabled={downloadingAcquittee}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                      background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#15803D',
                      cursor: 'pointer', opacity: downloadingAcquittee ? 0.5 : 1,
                    }}>
                    {downloadingAcquittee ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                    Facture acquittée
                  </button>
                )}
                {/* Envoyer la facture par email (Destinataire factures) */}
                {facture_ext_status === 'posted' && (
                  <button
                    data-testid="pl-draft-btn-envoyer-email"
                    onClick={handleEnvoyerFactureEmail}
                    disabled={envoyantFactureEmail}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                      background: 'none', border: '1px solid #0EA5E9', color: '#0EA5E9',
                      cursor: 'pointer', opacity: envoyantFactureEmail ? 0.5 : 1,
                    }}>
                    {envoyantFactureEmail ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                    Envoyer la facture par email
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Panel de saisie du règlement — portal au-dessus du panel PDF (zIndex 10000) */}
      {showReglementPanel && devis && typeof document !== 'undefined' && createPortal(
        /* Dialog imbriqué : Radix pause le FocusScope du Sheet parent → les inputs du panel deviennent éditables */
        <Dialog.Root open={true}>
          <Dialog.Content
            data-ignore-outside
            asChild
            onInteractOutside={e => e.preventDefault()}
            onOpenAutoFocus={e => e.preventDefault()}
            onCloseAutoFocus={e => e.preventDefault()}
          >
            <div style={{ position: 'fixed', inset: 0, zIndex: 10500, pointerEvents: 'none' }}>
              <ReglementPanel
                devis={devis}
                onClose={() => setShowReglementPanel(false)}
                onSaved={(updatedData) => {
                  // Mettre à jour l'état règlement : truthy si au moins un règlement existe
                  setReglementSaisi(updatedData?.reglements?.length > 0 ? updatedData : null);
                  setShowReglementPanel(false);
                }}
              />
            </div>
          </Dialog.Content>
        </Dialog.Root>,
        document.body
      )}

      {/* Modale d'agrégation des Commandes d'Achat */}
      {aggregationModalVente && (
        <AggregationCAModal
          candidats={aggregationModalVente.candidats}
          onClose={() => { setAggregationModalVente(null); setSendingCA(false); }}
          onConfirm={(choix) =>
            _doCommanderLignesVente(
              aggregationModalVente.lignes,
              choix,
              aggregationModalVente.source_id,
              aggregationModalVente.agence_id,
            )
          }
        />
      )}

      {/* Modale duplication — sibling du SidePanel pour éviter le FocusScope du Sheet Radix */}
      <Dialog.Root open={showDupliqueModal} onOpenChange={setShowDupliqueModal}>
        <Dialog.Portal>
          <Dialog.Overlay style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9998 }} />
          <Dialog.Content
            aria-describedby={undefined}
            style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 9999, background: 'var(--bg-card, #ffffff)', border: '1px solid var(--border, #e2e8f0)', borderRadius: 12, padding: 24, minWidth: 320, maxWidth: 400, outline: 'none' }}
            onPointerDownOutside={() => setShowDupliqueModal(false)}
          >
            <Dialog.Title style={{ fontWeight: 600, marginBottom: 16, color: 'var(--text-primary)', fontSize: 14 }}>Dupliquer le devis vers</Dialog.Title>
            {(() => {
              const agences = activeSociete?.agences || [];
              return <>
                {/* Liste d'agences cliquables — pas de <select> natif pour éviter les bugs Radix */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20, maxHeight: 220, overflowY: 'auto' }}>
                  {agences.map(a => (
                    <div
                      key={a.agence_id}
                      data-testid={`duplique-agence-option-${a.agence_id}`}
                      onClick={() => setDupliqueAgenceId(a.agence_id)}
                      style={{
                        padding: '9px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500,
                        border: `1.5px solid ${(dupliqueAgenceId || agences[0]?.agence_id) === a.agence_id ? '#2563EB' : 'var(--border, #e2e8f0)'}`,
                        background: (dupliqueAgenceId || agences[0]?.agence_id) === a.agence_id ? '#EFF6FF' : 'transparent',
                        color: (dupliqueAgenceId || agences[0]?.agence_id) === a.agence_id ? '#1D4ED8' : 'var(--text-primary)',
                        transition: 'all .12s',
                      }}
                    >
                      {a.nom}
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <button
                    onClick={() => setShowDupliqueModal(false)}
                    style={{ padding: '7px 18px', borderRadius: 8, border: '1px solid var(--border, #e2e8f0)', background: 'transparent', color: 'var(--text-muted, #64748b)', cursor: 'pointer', fontSize: 14 }}>
                    Annuler
                  </button>
                  <button
                    onClick={async () => {
                      const agences = activeSociete?.agences || [];
                      const targetAgenceId = dupliqueAgenceId || agences[0]?.agence_id;
                      setShowDupliqueModal(false);
                      try {
                        const r = await api.dupliquerDevis(devis.vente_id, targetAgenceId);
                        toast.success(`Devis ${r.data.devis?.numero || ''} créé`);
                        onSaved?.(r.data.devis);
                      } catch { toast.error('Erreur lors de la duplication'); }
                    }}
                    style={{ padding: '7px 18px', borderRadius: 8, background: '#1D4ED8', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
                    data-testid="duplique-confirmer-btn">
                    Dupliquer
                  </button>
                </div>
              </>;
            })()}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* Fenêtre envoi facture par email — Dialog.Portal gère le portal et le stacking */}
      <EmailComposeModal
        open={factureEmailPanel.open}
        onClose={() => setFactureEmailPanel(p => ({ ...p, open: false }))}
        onSend={handleConfirmFactureEmail}
        destinataires={factureEmailPanel.destinataires}
        loadingDestinataires={factureEmailPanel.loading}
        typeCode="facture_distribution"
        templateVariables={{
          nom_client:       devis?.entreprise_nom || devis?.contact_nom || '',
          numero_facture:   factureEmailPanel.factureMeta?.ext_invoice_number || factureEmailPanel.facture?.ext_invoice_number || '',
          date_facture:     (factureEmailPanel.factureMeta?.date_facture || factureEmailPanel.facture?.date_facture)
            ? new Date(factureEmailPanel.factureMeta?.date_facture || factureEmailPanel.facture?.date_facture).toLocaleDateString('fr-FR') : '',
          total_ttc:        (factureEmailPanel.factureMeta?.montant_ttc ?? factureEmailPanel.facture?.montant_ttc) != null
            ? `${Number(factureEmailPanel.factureMeta?.montant_ttc ?? factureEmailPanel.facture?.montant_ttc).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} EUR` : '',
          nom_emetteur:     currentUser ? (`${currentUser.prenom || ''} ${currentUser.nom || ''}`.trim() || currentUser.name || currentUser.email || '') : '',
          nom_destinataire: factureEmailPanel.destinataires?.[0]?.nom || '',
        }}
        agenceId={devis?.agence_id || ''}
        entrepriseId={factureEmailPanel.factureMeta?.entreprise_id || devis?.entreprise_id || null}
        titre="Envoyer la facture par email"
        documentRef={devis?.numero ? `Devis ${devis.numero}` : ''}
        sending={envoyantFactureEmail}
      />

      {showBlocageModal && (
        <BlocageClientModal
          blocage={blocageClient}
          entrepriseId={devis?.client_entity_id || devis?.client_id}
          onClose={() => setShowBlocageModal(false)}
        />
      )}

    </>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function Ventes() {
  const { isReadOnly, activeAgences } = useSociete();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalTtcSum, setTotalTtcSum] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ statut: '', search: '', statut_facturation: '', statut_livraison: '' });
  const [searchInput, setSearchInput] = useState('');
  const searchTimer = useRef(null);
  const [panel, setPanel] = useState(null); // null | { mode: 'new' | 'view', devis }
  const [sansBC, setSansBC] = useState(false);
  const [uniteDecimals, setUniteDecimals] = useState({});

  // Chargé une seule fois dans le parent → disponible avant l'ouverture de tout panneau
  useEffect(() => {
    api.getUnites().then(r => {
      const map = {};
      (r.data || []).forEach(u => { if (u.symbole) map[u.symbole] = u.nb_decimales ?? 0; });
      setUniteDecimals(map);
    }).catch(() => {});
  }, []);

  const COUNTS_INIT = { devis: 0, devis_envoye: 0, commande: 0, annule: 0 };
  const FACT_KEYS = Object.keys(FACT_STATUTS_CONFIG);
  const LIVR_KEYS = Object.keys(LIVR_STATUTS_CONFIG);
  const [counts, setCounts] = useState(COUNTS_INIT);
  const [factCounts, setFactCounts] = useState(Object.fromEntries(FACT_KEYS.map(k => [k, 0])));
  const [livrCounts, setLivrCounts] = useState(Object.fromEntries(LIVR_KEYS.map(k => [k, 0])));

  const load = useCallback(async () => {
    setLoading(true);
    const agence_id = activeAgences?.length === 1 ? activeAgences[0].agence_id : undefined;
    try {
      if (sansBC) {
        const r = await api.getEntitesSansBonCommande('devis');
        setData(r.data || []);
        setTotal((r.data || []).length);
      } else {
        // Params entonnoir : fact compte dans le contexte du statut BC actif
        const agenceParam = agence_id ? { agence_id } : {};
        const factBaseParams = { ...agenceParam, ...(filters.statut ? { statut: filters.statut } : {}) };
        const livrBaseParams = { ...factBaseParams, ...(filters.statut_facturation ? { statut_facturation: filters.statut_facturation } : {}) };

        const [r, ...countRes] = await Promise.all([
          api.getVentes({ ...filters, per_page: 100, ...agenceParam }),
          ...Object.keys(COUNTS_INIT).map(s => api.getVentes({ statut: s, per_page: 1, ...agenceParam })),
          ...FACT_KEYS.map(s => api.getVentes({ statut_facturation: s, per_page: 1, ...factBaseParams })),
          ...LIVR_KEYS.map(s => api.getVentes({ statut_livraison: s, per_page: 1, ...livrBaseParams })),
        ]);
        setData(r.data.data || []);
        setTotal(r.data.total || 0);
        setTotalTtcSum(r.data.total_ttc_sum || 0);
        const bcKeys = Object.keys(COUNTS_INIT);
        setCounts(Object.fromEntries(bcKeys.map((k, i) => [k, countRes[i].data.total || 0])));
        const factOffset = bcKeys.length;
        setFactCounts(Object.fromEntries(FACT_KEYS.map((k, i) => [k, countRes[factOffset + i].data.total || 0])));
        const livrOffset = factOffset + FACT_KEYS.length;
        setLivrCounts(Object.fromEntries(LIVR_KEYS.map((k, i) => [k, countRes[livrOffset + i].data.total || 0])));
      }
    } catch { toast.error('Erreur chargement des ventes'); }
    finally { setLoading(false); }
  }, [filters, activeAgences, sansBC]);

  useEffect(() => { load(); }, [load]);

  // ── Ouverture directe d'un devis via URL ?open_devis=DEVIS_ID ────────────────
  useEffect(() => {
    const venteId = searchParams.get('open_devis');
    if (!venteId) return;
    const found = data.find(d => d.vente_id === venteId);
    if (found) { setPanel({ mode: 'view', devis: found }); setSearchParams({}, { replace: true }); return; }
    api.getVente(venteId)
      .then(r => { if (r.data) { setPanel({ mode: 'view', devis: r.data }); setSearchParams({}, { replace: true }); } })
      .catch(() => {});
  }, [searchParams, data]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => setFilters(f => ({ ...f, search: searchInput }));
  const handleStatutFilter = (s) => setFilters(f => ({ ...f, statut: f.statut === s ? '' : s }));
  const handleFactFilter = (s) => setFilters(f => ({ ...f, statut_facturation: f.statut_facturation === s ? '' : s }));
  const handleLivrFilter = (s) => setFilters(f => ({ ...f, statut_livraison: f.statut_livraison === s ? '' : s }));
  const filterIds = searchParams.get('filter_ids')?.split(',').filter(Boolean) || null;
  const filterSourceLabel = searchParams.get('source_label') || '';

  return (
    <div className="min-h-screen p-6 space-y-6" style={{ background: 'var(--background)' }}>

      {/* En-tête */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Ventes</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Devis et commandes clients</p>
        </div>
        <button onClick={() => setPanel({ mode: 'new', devis: null })}
          data-testid="vente-nouveau-btn"
          disabled={isReadOnly}
          title={isReadOnly ? 'Sélectionnez une société et une agence pour créer un devis' : ''}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: 'var(--primary-gradient)' }}>
          <Plus size={15} />Nouveau devis
        </button>
      </div>

      {/* Stats par statut BC */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {/* Carte Tous */}
        <button onClick={() => setFilters(f => ({ ...f, statut: '' }))}
          data-testid="vente-stat-tous"
          className="rounded-xl p-4 text-left border transition-all hover:shadow-md"
          style={{
            background: filters.statut === '' ? '#F8FAFC' : 'var(--surface)',
            borderColor: filters.statut === '' ? '#94A3B8' : 'var(--border)',
          }}>
          <div className="text-2xl font-bold" style={{ color: '#475569' }}>
            {Object.values(counts).reduce((a, b) => a + b, 0)}
          </div>
          <div className="text-xs font-medium mt-0.5" style={{ color: 'var(--text-secondary)' }}>Tous</div>
        </button>
        {Object.entries(STATUTS).map(([key, s]) => (
          <button key={key} onClick={() => handleStatutFilter(key)}
            data-testid={`vente-stat-${key}`}
            className="rounded-xl p-4 text-left border transition-all hover:shadow-md"
            style={{
              background: filters.statut === key ? s.bg : 'var(--surface)',
              borderColor: filters.statut === key ? s.border : 'var(--border)',
            }}>
            <div className="text-2xl font-bold" style={{ color: s.color }}>{counts[key]}</div>
            <div className="text-xs font-medium mt-0.5" style={{ color: 'var(--text-secondary)' }}>{s.label}</div>
          </button>
        ))}
      </div>

      {/* Filtres Facturation + Livraison */}
      <div className="grid grid-cols-2 gap-3">
        {/* Bloc Facturation */}
        <div className="flex items-center justify-center gap-3 py-2.5 px-4 rounded-xl"
          style={{ background: 'var(--surface)' }}>
          <span className="text-xs font-semibold uppercase tracking-widest shrink-0"
            style={{ color: 'var(--text-muted)' }}>Facturation</span>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(FACT_STATUTS_CONFIG).map(([key, s]) => {
              const active = filters.statut_facturation === key;
              return (
                <button key={key}
                  data-testid={`vente-fact-${key}`}
                  onClick={() => handleFactFilter(key)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all"
                  style={{
                    background: active ? s.bg : 'var(--background)',
                    borderColor: active ? s.color : 'var(--border)',
                    color: active ? s.color : 'var(--text-muted)',
                    boxShadow: active ? `0 0 0 2px ${s.border}` : 'none',
                    fontWeight: active ? 700 : 500,
                  }}>
                  <span style={{ color: s.color, fontWeight: 700 }}>{factCounts[key]}</span>
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Bloc Livraison */}
        <div className="flex items-center justify-center gap-3 py-2.5 px-4 rounded-xl"
          style={{ background: 'var(--surface)' }}>
          <span className="text-xs font-semibold uppercase tracking-widest shrink-0"
            style={{ color: 'var(--text-muted)' }}>Livraison</span>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(LIVR_STATUTS_CONFIG).map(([key, s]) => {
              const active = filters.statut_livraison === key;
              return (
                <button key={key}
                  data-testid={`vente-livr-${key}`}
                  onClick={() => handleLivrFilter(key)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all"
                  style={{
                    background: active ? s.bg : 'var(--background)',
                    borderColor: active ? s.color : 'var(--border)',
                    color: active ? s.color : 'var(--text-muted)',
                    boxShadow: active ? `0 0 0 2px ${s.border}` : 'none',
                    fontWeight: active ? 700 : 500,
                  }}>
                  <span style={{ color: s.color, fontWeight: 700 }}>{livrCounts[key]}</span>
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl border bg-white flex-1 min-w-48"
          style={{ borderColor: 'var(--border)' }}>
          <Search size={14} style={{ color: 'var(--text-muted)' }} />
          <input value={searchInput} onChange={e => { const v = e.target.value; setSearchInput(v); clearTimeout(searchTimer.current); searchTimer.current = setTimeout(() => setFilters(f => ({ ...f, search: v })), 300); }}
            placeholder="N° devis, client, référence…"
            className="flex-1 text-sm focus:outline-none" style={{ color: 'var(--text)', background: 'transparent' }}
            data-testid="vente-search-input" />
          {searchInput && (
            <button onClick={() => { setSearchInput(''); setFilters(f => ({ ...f, search: '' })); }}>
              <X size={12} style={{ color: 'var(--text-muted)' }} />
            </button>
          )}
        </div>
        {/* Toggle Sans bon de commande */}
        <button
          data-testid="vente-toggle-sans-bc"
          onClick={() => { setSansBC(v => !v); setSearchInput(''); setFilters({ statut: '', search: '', statut_facturation: '', statut_livraison: '' }); }}
          title="Afficher uniquement les devis sans bon de commande joint"
          className="flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-semibold transition-all"
          style={{
            borderColor: sansBC ? '#DC2626' : 'var(--border)',
            background: sansBC ? '#FEF2F2' : 'var(--surface)',
            color: sansBC ? '#DC2626' : 'var(--text-muted)',
          }}
        >
          <FolderOpen size={14} />
          Sans bon de commande
          {sansBC && (
            <span className="text-xs font-bold px-1.5 py-0.5 rounded-full"
              style={{ background: '#DC2626', color: '#fff' }}>
              {total}
            </span>
          )}
        </button>
        <button onClick={load} className="p-2 rounded-xl border hover:bg-gray-50 transition-colors"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
          <RefreshCw size={14} />
        </button>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{total} document(s)</span>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary-solid)' }} />
        </div>
      ) : (
        <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 120 }} /> {/* N° Devis */}
                <col style={{ width: 300 }} /> {/* Client */}
                <col style={{ width: 110 }} /> {/* Titre */}
                <col style={{ width: 90  }} /> {/* Date */}
                <col style={{ width: 110 }} /> {/* Total TTC */}
                <col style={{ width: 60  }} /> {/* Lignes */}
                <col style={{ width: 100 }} /> {/* Statut */}
                <col style={{ width: 120 }} /> {/* Facturation */}
                <col style={{ width: 100 }} /> {/* Livraison */}
                <col style={{ width: 80  }} /> {/* Suivi par */}
              </colgroup>
              {/* Bannière filtre smart button */}
              {filterIds && (
                <caption style={{ captionSide: 'top', background: '#EFF6FF', borderBottom: '1px solid #BFDBFE', padding: '8px 20px', textAlign: 'left' }}>
                  <span className="text-xs font-medium" style={{ color: '#1D4ED8' }}>
                    Vue filtrée — {filterIds.length} vente{filterIds.length > 1 ? 's' : ''} liée{filterIds.length > 1 ? 's' : ''} à <strong>{filterSourceLabel}</strong>
                  </span>
                  <button type="button"
                    onClick={() => setSearchParams({}, { replace: true })}
                    className="ml-4 text-xs font-semibold px-3 py-0.5 rounded-full hover:bg-blue-100"
                    style={{ color: '#1D4ED8' }}>
                    × Effacer le filtre
                  </button>
                </caption>
              )}
              <thead>
                <tr style={{ background: '#FAFAFA', borderBottom: '1px solid var(--border)' }}>
                  {['N° Devis', 'Client', 'Titre', 'Date', 'Total TTC', 'Lignes', 'Statut', 'Facturation', 'Livraison', 'Suivi par'].map(h => (
                    <th key={h}
                      className={`${h === 'N° Devis' ? 'px-2' : 'px-5'} py-3 text-xs font-semibold uppercase tracking-wide whitespace-nowrap ${['Statut','Facturation','Livraison'].includes(h) ? 'text-center' : 'text-left'}`}
                      style={{ color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr><td colSpan={10} className="px-5 py-16 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                    Aucun document — créez votre premier devis avec le bouton "Nouveau devis"
                  </td></tr>
                ) : (filterIds ? data.filter(d => filterIds.includes(d.vente_id)) : data).map((d, i) => (
                  <tr key={d.vente_id}
                    data-testid={`vente-row-${d.vente_id}`}
                    onClick={() => setPanel({ mode: 'view', devis: d })}
                    className="cursor-pointer transition-colors hover:bg-blue-50/40"
                    style={{ borderBottom: i < data.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                    <td className="px-2 py-3 text-sm whitespace-nowrap overflow-hidden" style={{ color: 'var(--text)', textOverflow: 'ellipsis' }}>
                      {d.numero}
                    </td>
                    <td className="px-5 py-3" style={{ color: 'var(--text)' }}>
                      <span className="font-semibold">{d.entreprise_nom || '—'}</span>
                    </td>
                    <td className="px-5 py-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                      <span className="truncate block max-w-xs" title={d.titre || ''}>
                        {d.titre || '—'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                      {fmtDate(d.date_devis)}
                    </td>
                    <td className="px-5 py-3 font-bold text-right whitespace-nowrap" style={{ color: 'var(--text)' }}>
                      {fmt(d.total_ttc)} €
                    </td>
                    <td className="px-5 py-3 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                      {(d.lignes || []).length}
                    </td>
                    <td className="px-5 py-3 text-center">
                      <StatutBadge statut={d.statut} />
                    </td>
                    <td className="px-5 py-3 text-center">
                      {d.statut_facturation && d.statut_facturation !== 'non_facturee' ? (() => {
                        const cfg = {
                          en_attente_paiement: { label: 'En attente', bg: '#FFF7ED', color: '#B45309', border: '#FCD34D' },
                          validee:             { label: 'Validée',    bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
                          payee:               { label: 'Payée',      bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
                          partiellement_payee: { label: 'Part. payée',bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
                          annulee:             { label: 'Annulée',    bg: '#FEF2F2', color: '#DC2626', border: '#FECACA' },
                        }[d.statut_facturation];
                        if (!cfg) return '—';
                        return (
                          <span style={{ display:'inline-flex', alignItems:'center', background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, borderRadius: '100px', padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {cfg.label}
                          </span>
                        );
                      })() : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td className="px-5 py-3 text-center">
                      {d.statut_livraison && d.statut_livraison !== 'non_livree' ? (() => {
                        const cfg = {
                          en_cours: { label: 'En cours', bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA' },
                          partiel:  { label: 'Partiel',  bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
                          livre:    { label: 'Livré',    bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
                        }[d.statut_livraison];
                        if (!cfg) return '—';
                        return (
                          <span style={{ display:'inline-flex', alignItems:'center', background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, borderRadius: '100px', padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {cfg.label}
                          </span>
                        );
                      })() : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td className="px-5 py-3 text-xs whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                      {(d.suivi_par_nom || '').split(' ')[0] || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              {totalTtcSum > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--border)', background: '#F8FAFC' }}>
                    <td colSpan={4} />
                    <td className="px-5 py-3 text-sm font-bold text-right" style={{ color: 'var(--text)' }}>
                      {(() => {
                        const [int, dec] = (Number(totalTtcSum) || 0).toFixed(2).split('.');
                        return int.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0') + ',' + dec;
                      })()} €
                    </td>
                    <td colSpan={5} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {/* Side Panel */}
      {panel && (
        <VenteSidePanel
          key={panel.devis?.vente_id ?? 'new'}
          devis={panel.devis}
          onClose={() => setPanel(null)}
          onSaved={(newDevis) => { if (newDevis?.vente_id) { setPanel({ mode: 'view', devis: newDevis }); load(); } else { setPanel(null); load(); } }}
          uniteDecimals={uniteDecimals}
        />
      )}
    </div>
  );
}
